import { useParams, useLocation } from "react-router-dom";
import { decodeRefNum } from "../utils/refNumCrypto";
import { countryFromSearch } from "../utils/caseHref";
import { hasNoLocationPermissionWarning } from "../utils/warnings";
import { useState, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  getAuthToken,
  getUserProfile,
  getSourceName,
} from "../services/authService";
import { getClientEntry, getApiSourceName } from "../config/clientConfig";
import { buildMediaUrl } from "../utils/mediaUrl";
import { getStatusInfo, isFailedStatus, formatDateTime } from "../utils/statusMapping";
import { formatUploadDuration } from "../utils/uploadDuration";
import { getAssessmentDetail } from "../services/apiService";
import { getApiGeneration } from "../services/apiGeneration";
import { usePageConfig } from "../hooks/usePageConfig";
import {
  getApiBaseUrl,
  PORTAL_BASE_URL,
  withTrailingSlash,
} from "../config/urls";

// Import local assets
// Vehicle images for fallback
import exteriorDamage1 from "../assets/images/exterior-damage-1.png";
import closeupDamage1 from "../assets/images/closeup-damage-1.png";
import identifierDamage1 from "../assets/images/identifier-damage-1.png";
import aiAssessedIcon from "../assets/icons/brain-status.svg";
import magicWandIcon from "../assets/icons/magic-wand.svg";
import glassesIcon from "../assets/icons/glasses.svg";
import imageIcon from "../assets/icons/image.svg";
import folderIcon from "../assets/icons/folder.svg";
import warningTriangleIcon from "../assets/icons/warning-triangle-red.svg";
import cameraIcon from "../assets/icons/camera-01.svg";
import searchIcon from "../assets/icons/search-icon.svg";
import severeDot from "../assets/icons/severe-dot.svg";
import moderateDot from "../assets/icons/moderate-dot.svg";
import minorDot from "../assets/icons/minor-dot.svg";
import arrowRightGray from "../assets/icons/arrow-right-gray.svg";
import checkCircleGreen from "../assets/icons/check-circle-green-figma.svg";

import documentIcon from "../assets/icons/document-icon.svg";
import downloadIcon from "../assets/icons/download-icon.svg";
import loadingIcon from "../assets/icons/cases/loading.svg";
import latviaFlag from "../assets/icons/cases/latvia-flag.svg";
import lithuaniaFlag from "../assets/icons/cases/lithuania-flag.svg";
import estoniaFlag from "../assets/icons/cases/estonia-flag.svg";

// Import components
import ImageLightbox from "../components/ImageLightbox";
import DamagedPartsModal from "../components/DamagedPartsModal";

// Import professional car stencils from Figma
import {
  CarStencilFront,
  CarStencilRight,
  CarStencilBack,
  CarStencilLeft,
} from "../components/CarStencils";
import CaptureIssues from "../components/CaptureIssues";
import LocationCheckModal from "../components/LocationCheckModal";
import CarDamageAssessment from "./CarDamageAssessment";
import ApproveCommentModal from "../components/ApproveCommentModal";
import { OnboardingTour, TOUR_STEPS } from "../onboarding";
import OverviewIdentifiedModal from "../components/OverviewIdentifiedModal";
import { placeholderFor } from "../utils/caseThumbnail";

const DOCUMENTS_CACHE_NAME = "documents-tab-cache-v1";
const DOCUMENTS_CACHE_TTL_MS = 30 * 60 * 1000;

const COUNTRY_FLAGS = {
  latvia: latviaFlag,
  lv: latviaFlag,
  lat: latviaFlag,
  lithuania: lithuaniaFlag,
  lt: lithuaniaFlag,
  ltu: lithuaniaFlag,
  estonia: estoniaFlag,
  ee: estoniaFlag,
  est: estoniaFlag,
};

/** @returns {string} flag asset URL, or "" when the country is unknown. */
const getCountryFlagSrc = (countryName) =>
  COUNTRY_FLAGS[String(countryName || "").toLowerCase().trim()] || "";

const normalizeArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const uniqueValues = (values) => {
  const seen = new Set();

  return values
    .map((value) => (value ?? "").toString().trim())
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (key === "unknown" || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const STANDARD_SIDE_OPTIONS = [
  { key: "front", label: "Front" },
  { key: "frontleft", label: "Front Left" },
  { key: "frontright", label: "Front Right" },
  { key: "left", label: "Left" },
  { key: "right", label: "Right" },
  { key: "rear", label: "Rear" },
  { key: "rearleft", label: "Rear Left" },
  { key: "rearright", label: "Rear Right" },
];

const SIDE_LABEL_BY_KEY = STANDARD_SIDE_OPTIONS.reduce((labels, side) => {
  labels[side.key] = side.label;
  return labels;
}, {});

const normalizeOverviewSideKey = (side) => {
  const rawSide =
    typeof side === "string"
      ? side
      : side?.side || side?.name || side?.label || "";

  const compact = rawSide
    .toString()
    .trim()
    .toLowerCase()
    .replace(/([a-z])([A-Z])/g, "$1$2")
    .replace(/[\s_-]+/g, "")
    .replace(/^back/, "rear");

  if (SIDE_LABEL_BY_KEY[compact]) return compact;
  return "";
};

const getValidOverviewSideLabel = (side) => {
  const key = normalizeOverviewSideKey(side);
  return key ? SIDE_LABEL_BY_KEY[key] : "";
};

// Figma-aligned Case Details view (Tailwind). Sidebar removed to use app's global Layout.
export default function CaseDetails() {
  const { pageConfig, clientConfig, mediaBaseUrl: configMediaBaseUrl } = usePageConfig();
  // Resolved fresh rather than from usePageConfig()'s clientConfig: that
  // value is apiConfig?.client (the portal-config API's record) OR the
  // static per-client file — a plain fallback, not a merge, so once the API
  // answers at all, any field its record doesn't state (this one included)
  // is simply gone rather than filled in from the static file. getApiSourceName()
  // checks the logged-in client's own registry entry first instead, which is
  // why every other caller of /assessment_new-style endpoints in apiService.js
  // (getEffectiveSourceName) already resolves it this way.
  const apiSourceName = getApiSourceName();

  const { caseId: _encodedCaseId } = useParams();
  const routerLocation = useLocation();
  const caseId = decodeRefNum(_encodedCaseId);

  const [tab, setTab] = useState(pageConfig.general.defaultTab); // ai | overview | images | documents | poi | passport
  const [damageTab, setDamageTab] = useState("inventory"); // inventory | part-wise
  const [currentVehicleView, setCurrentVehicleView] = useState(0); // 0: front, 1: right, 2: rear, 3: left
  const [assessmentData, setAssessmentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [aiSummaryData, setAiSummaryData] = useState(null);
  const [isAiSummaryLoading, setIsAiSummaryLoading] = useState(false);
  // Point of Impact / Digital Passport — client-gated tabs, fetched on first open
  const [poiData, setPoiData] = useState(null);
  const [isPoiLoading, setIsPoiLoading] = useState(false);
  const [passportData, setPassportData] = useState(null);
  const [isPassportLoading, setIsPassportLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxCurrentIndex, setLightboxCurrentIndex] = useState(0);

  const [isSearchActive, setIsSearchActive] = useState(false);

  const [lightboxIsDamageImages, setLightboxIsDamageImages] = useState(false);
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const [documentsPreviewUrl, setDocumentsPreviewUrl] = useState("");
  const [documentsPreviewError, setDocumentsPreviewError] = useState("");
  const [documentsArchiveName, setDocumentsArchiveName] = useState("");
  const [isReportDownloading, setIsReportDownloading] = useState(false);

  const shouldCollapseVehicleStencil =
    tab === "images" || tab === "documents";
  const isApproved =
    parseInt(assessmentData?.info?.claim_state) === 8;

  /**
   * A failed capture has no point of impact and no history worth showing —
   * both tabs would describe damage on a vehicle that was never assessed. The
   * rule is about the case, so it applies whatever the client's config enables.
   */
  const caseFailed = isFailedStatus(assessmentData?.info?.claim_state);
  const isDocumentsTabEnabled = isApproved;

  // "NONE"/"null" are real values this API sends for an unset field
  // (confirmed on vin_reading and on make/model respectively — the latter
  // otherwise renders the header as the literal text "null null"), not the
  // words as data — same idea as property's lat/long arriving as the literal
  // string "None" for "no coordinates".
  const cleanOrFallback = (value, fallback) => {
    const str = value === undefined || value === null ? "" : String(value).trim();
    return str && !["none", "null"].includes(str.toLowerCase()) ? str : fallback;
  };
  const displayOrDash = (value) => cleanOrFallback(value, "—");

  const vehicleDetailValues = {
    brand: cleanOrFallback(assessmentData?.info?.make, "N/A"),
    model: cleanOrFallback(assessmentData?.info?.model, "N/A"),
    mfgYear: cleanOrFallback(assessmentData?.info?.year, "N/A"),
    series: cleanOrFallback(assessmentData?.info?.series, "N/A"),
  };

  // Same field priority as the redesign's equivalent header
  // (CaseDetailsRedesign.js) — plus the same fallback: a real captured
  // /assessment_new response carries no country field at all (only a
  // numeric country_id), so this also reads the ?country= the list passed
  // along (see Cases.js's handleCaseClick / utils/caseHref.js).
  const countryName =
    assessmentData?.info?.country_corp_id ||
    assessmentData?.info?.country ||
    assessmentData?.info?.country_name ||
    countryFromSearch(routerLocation.search) ||
    "";
  const countryFlagSrc = getCountryFlagSrc(countryName);

  // "00 hour 01 minutes 32 seconds" — sent pre-formatted, confirmed by a
  // captured /assessment_new response; shown as-is rather than re-parsed.
  const timeTakenToUpload = assessmentData?.info?.time_taken_to_upload || "";

  const headerVehicleDetails = [
    { label: "Brand", value: vehicleDetailValues.brand },
    { label: "Model", value: vehicleDetailValues.model },
    { label: "Mfg. Year", value: vehicleDetailValues.mfgYear },
    { label: "Series", value: vehicleDetailValues.series },
  ];

  // Approve flow: capture reviewer comment
  const [approveCommentOpen, setApproveCommentOpen] = useState(false);

  // Damaged Parts Modal state
  const [damagedPartsModalState, setDamagedPartsModalState] = useState({
    isOpen: false,
    initialFilter: "all",
    initialSeverityFilter: "",
  });
  const [overviewModalType, setOverviewModalType] = useState(null);

  // Helper functions to open modal with specific filters
  const openDamagedPartsModal = (
    initialFilter = "all",
    initialSeverityFilter = "",
  ) => {
    setDamagedPartsModalState({
      isOpen: true,
      initialFilter,
      initialSeverityFilter,
    });
  };

  const closeDamagedPartsModal = () => {
    setDamagedPartsModalState({
      isOpen: false,
      initialFilter: "all",
      initialSeverityFilter: "",
    });
  };

  const closeOverviewModal = () => {
    setOverviewModalType(null);
  };

  const mediaBaseUrl = configMediaBaseUrl;

  const mediaPathPrefix = clientConfig?.mediaPathPrefix || "";

  const getMediaUrl = (path) =>
    buildMediaUrl(path, { baseUrl: mediaBaseUrl, pathPrefix: mediaPathPrefix });

  const normalizeImageSide = (side) =>
    (side || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "")
      .replace(/^back/, "rear");

  const getImageIdentity = (image) => {
    const imageUrl = (image.imageUrl || image.src || "").toString().trim();

    if (imageUrl) {
      try {
        const url = new URL(imageUrl, mediaBaseUrl);
        return `${url.origin}${url.pathname}`;
      } catch (error) {
        return imageUrl.split(/[?#]/)[0];
      }
    }

    const normalizedSide = normalizeImageSide(image.side || image.title);
    return normalizedSide && image.id ? `${normalizedSide}|${image.id}` : "";
  };

  const getUniqueImages = (images) => {
    const seen = new Set();

    return images.filter((image) => {
      const key = getImageIdentity(image);
      if (!key) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const getUploadedImageCount = () =>
    normalizeArray(assessmentData?.uploads).filter(
      (upload) => upload?.upload_picture || upload?.picture,
    ).length;

  const getProcessedDamageImagePath = (image, includeUploadPicture = false) =>
    image?.processed_picture ||
    image?.annotated_picture ||
    image?.annotated_image ||
    (includeUploadPicture ? image?.upload_picture : "") ||
    image?.picture ||
    image?.imageUrl;

  const getOriginalDamageImagePath = (image, includePicture = false) =>
    image?.upload_picture ||
    image?.original_picture ||
    image?.original_uploaded_img_path ||
    (includePicture ? image?.picture : "") ||
    "";

  const partsOverview = useMemo(() => {
    const summary = assessmentData?.summary || {};

    const unidentified = uniqueValues([
      ...normalizeArray(summary.non_identified_parts_list),
      ...normalizeArray(summary.unidentified_parts_list),
      ...normalizeArray(summary.unidentified_parts),
    ]);

    const unidentifiedKeys = new Set(
      unidentified.map((part) => part.toLowerCase())
    );

    const identified = uniqueValues([
      ...normalizeArray(summary.identified_parts_list),
      ...normalizeArray(summary.parts_identified_list),
      ...normalizeArray(summary.identified_parts),
    ]).filter((part) => !unidentifiedKeys.has(part.toLowerCase()));

    return { identified, unidentified };
  }, [assessmentData]);

  const sidesOverview = useMemo(() => {
    const summary = assessmentData?.summary || {};
    const assessmentDetails = normalizeArray(assessmentData?.assessment_details);
    const uploads = normalizeArray(assessmentData?.uploads);
    const roiImages = normalizeArray(assessmentData?.roi_images);
    const allDamages = normalizeArray(assessmentData?.all_damages);
    const warnings = assessmentData?.warnings || {};

    const getValidSideLabels = (values) => uniqueValues(
      values.map(getValidOverviewSideLabel).filter(Boolean)
    );

    const apiUnidentified = getValidSideLabels([
      ...normalizeArray(summary.non_identified_sides_list),
      ...normalizeArray(summary.unidentified_sides_list),
      ...normalizeArray(summary.unidentified_sides),
      ...normalizeArray(summary.missing_sides),
    ]);

    const hasPartialCaptures =
      normalizeArray(warnings.partial_sides).some(normalizeOverviewSideKey);

    const identified = getValidSideLabels([
      ...normalizeArray(summary.identified_sides_list),
      ...normalizeArray(summary.sides_identified_list),
      ...normalizeArray(summary.identified_sides),
      ...assessmentDetails.map((part) => part?.side),
      ...uploads.map((upload) => upload?.side),
      ...roiImages.map((image) => image?.side),
      ...allDamages.map((damage) => damage?.side),
    ]);

    const sideUniverse = hasPartialCaptures
      ? getValidSideLabels([
        ...STANDARD_SIDE_OPTIONS.map((side) => side.label),
        ...identified,
        ...apiUnidentified,
        ...normalizeArray(warnings.partial_sides),
        ...assessmentDetails.map((part) => part?.side),
        ...uploads.map((upload) => upload?.side),
        ...roiImages.map((image) => image?.side),
        ...allDamages.map((damage) => damage?.side),
      ])
      : STANDARD_SIDE_OPTIONS.map((side) => side.label);

    const identifiedKeys = new Set(
      identified.map((side) => normalizeOverviewSideKey(side))
    );

    const unidentified = sideUniverse.filter((side) => {
      const key = normalizeOverviewSideKey(side);
      return key && !identifiedKeys.has(key);
    });

    return {
      identified,
      unidentified,
    };
  }, [assessmentData]);

  const partsModalSections = useMemo(
    () => [
      {
        title: "Identified Parts",
        icon: checkCircleGreen,
        items: partsOverview.identified.map((part) => ({ label: part })),
        emptyMessage: "No identified parts",
      },
      {
        title: "Unidentified Parts",
        icon: warningTriangleIcon,
        items: partsOverview.unidentified.map((part) => ({ label: part })),
        emptyMessage: "No unidentified parts",
      },
    ],
    [partsOverview]
  );

  const sidesModalSections = useMemo(
    () => [
      {
        title: "Identified Sides",
        items: sidesOverview.identified.map((side) => ({
          label: side,
          icon: checkCircleGreen,
        })),
        emptyMessage: "No identified sides",
      },
      {
        title: "Unidentified Sides",
        items: sidesOverview.unidentified.map((side) => ({
          label: side,
          icon: warningTriangleIcon,
        })),
        emptyMessage: "No unidentified sides",
      },
    ],
    [sidesOverview]
  );

  const handleApproveClick = () => {
    setApproveCommentOpen(true);
  };

  const handleApproveSubmit = async (comment) => {
    const token = getAuthToken();
    if (!token) {
      console.error("No authentication token available");
      return;
    }

    const profile = getUserProfile();
    const userEmail = profile?.email || clientConfig.fallbackEmail;

    const refNum = assessmentData?.info?.ref_num || caseId;
    const vehicleId = assessmentData?.info?.id;

    if (!vehicleId) {
      console.error("Missing vehicle id (assessmentData.info.id)");
      return;
    }

    const payload = {
      data: [],
      hello: "world",
      reviewerComment: {
        comment: comment,
        ref_num: refNum,
        source_name: apiSourceName,
        vehicle_id: vehicleId,
        user_id: userEmail,
        user_type: 10,
      },
      verified_list: [],
      approve: true,
    };

    try {
      const reviewHistoryReq = fetch(
        `${getApiBaseUrl()}/assess_update_review_history`,
        {
          method: "POST",
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          },
          body: `data=${encodeURIComponent(JSON.stringify(payload))}`,
        },
      );

      const vehicleUpdateBody = new URLSearchParams({
        keyValue: JSON.stringify({ claim_state: 8 }),
        ref_num: refNum,
        source_name: apiSourceName,
        user_id: userEmail,
      });

      const vehicleUpdateReq = fetch(`${getApiBaseUrl()}/vehicle/${vehicleId}/`, {
        method: "PUT",
        headers: {
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        },
        body: vehicleUpdateBody.toString(),
      });

      const [reviewRes, vehicleRes] = await Promise.all([
        reviewHistoryReq,
        vehicleUpdateReq,
      ]);

      const reviewOk = reviewRes.ok;
      const vehicleOk = vehicleRes.ok;

      if (!reviewOk) {
        const text = await reviewRes.text().catch(() => "");
        console.error("Failed to submit approve note:", reviewRes.status, text);
      }

      if (!vehicleOk) {
        const text = await vehicleRes.text().catch(() => "");
        console.error(
          "Failed to update vehicle claim_state:",
          vehicleRes.status,
          text,
        );
      }

      // Refresh case details only when both APIs succeeded
      if (reviewOk && vehicleOk) {
        callAssessmentAPI(refNum).catch((e) =>
          console.error("Error refreshing assessment after approval:", e),
        );
      }
    } catch (e) {
      console.error("Error submitting approval APIs:", e);
    }
  };

  // Function to refresh assessment data after damage updates
  const refreshAssessmentData = async () => {
    if (caseId) {
      await callAssessmentAPI(caseId);
    }
  };

  // API call function for assessment
  const callAssessmentAPI = async (refNum) => {
    setIsLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token available");
      }

      // v2 (Hegic) speaks a different contract entirely — GET /assessment with
      // a differently-shaped payload — so it is fetched and normalised in
      // apiService rather than branched through the request below.
      if (getApiGeneration() === "v2") {
        const normalized = await getAssessmentDetail(refNum);
        setAssessmentData(normalized);
        return normalized;
      }

      const body = new URLSearchParams({ ref_num: refNum, source_name: apiSourceName });



      const response = await fetch(`${getApiBaseUrl()}/assessment_new`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body,
      });

      if (response.ok) {
        const data = await response.json();
        setAssessmentData(data);
        return data;
      } else {
        const errText = await response.text().catch(() => "");
        console.error("Assessment API Error:", response.status, response.statusText, errText);
        if (response.status === 401) {
        }
      }
    } catch (error) {
      console.error("Assessment API Call Failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // API call function for AI summary
  const callAISummaryAPI = async (refNum) => {
    setIsAiSummaryLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token available");
      }

      const body = new URLSearchParams({ ref_num: refNum, source_name: apiSourceName });



      const response = await fetch(`${getApiBaseUrl()}/ai_summary`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body,
      });

      if (response.ok) {
        const data = await response.json();
        const transformedData = transformAiSummaryData(data);
        setAiSummaryData(transformedData);
        return data;
      } else {
        const errText = await response.text().catch(() => "");
        console.error("AI Summary API Error:", response.status, response.statusText, errText);
        if (response.status === 401) {
        }
      }
    } catch (error) {
      console.error("AI Summary API Call Failed:", error);
    } finally {
      setIsAiSummaryLoading(false);
    }
  };

  // API call function for Point of Impact
  const callPoiAPI = async (refNum) => {
    setIsPoiLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token available");
      }

      const body = new URLSearchParams({ ref_num: refNum, source_name: apiSourceName });

      const response = await fetch(`${getApiBaseUrl()}/poi`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body,
      });

      if (response.ok) {
        const data = await response.json();
        setPoiData(data);
        return data;
      }

      const errText = await response.text().catch(() => "");
      console.error("POI API Error:", response.status, response.statusText, errText);
    } catch (error) {
      console.error("POI API Call Failed:", error);
    } finally {
      setIsPoiLoading(false);
    }
  };

  // API call function for Digital Passport
  const callDigitalPassportAPI = async (refNum) => {
    setIsPassportLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token available");
      }

      const query = new URLSearchParams({ ref_num: refNum, source_name: apiSourceName });

      const response = await fetch(
        `${getApiBaseUrl()}/assessment/digital_passport?${query}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        // API wraps the record in a single-element `result` array
        setPassportData(Array.isArray(data?.result) ? data.result[0] || null : null);
        return data;
      }

      const errText = await response.text().catch(() => "");
      console.error(
        "Digital Passport API Error:",
        response.status,
        response.statusText,
        errText,
      );
    } catch (error) {
      console.error("Digital Passport API Call Failed:", error);
    } finally {
      setIsPassportLoading(false);
    }
  };

  // "front_left bumper" -> "Front Left Bumper"; tolerates arrays and nullish input.
  const titleCase = (value) => {
    const text = Array.isArray(value) ? value.join(", ") : value;
    if (!text) return "";
    return text
      .toString()
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (ch) => ch.toUpperCase());
  };

  // `damages` arrives as a JSON-encoded string, not an array.
  const passportDamages = useMemo(() => {
    if (!passportData?.damages) return [];
    if (Array.isArray(passportData.damages)) return passportData.damages;
    try {
      const parsed = JSON.parse(passportData.damages);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      console.warn("Digital Passport: could not parse `damages`");
      return [];
    }
  }, [passportData]);

  // Transform AI summary API response to our card format
  const transformAiSummaryData = (rawData) => {
    if (!rawData) return null;

    return {
      vehicle_overview: {
        details: [
          `${rawData.car_details?.make || "Unknown"} ${rawData.car_details?.model || "Unknown"
            } ${rawData.car_details?.vehicle_type || ""}`.trim(),
          `Primary Color: ${rawData.color_info?.primary_color || "Unknown"} ${rawData.color_info?.is_metallic
              ? "(Metallic)"
              : rawData.color_info?.is_matte
                ? "(Matte)"
                : ""
            }`.trim(),
          `Origin: ${rawData.car_details?.origin || "Unknown"}`,
          `Engine Status: ${rawData.vehicle_readings?.engine_working ? "Working" : "Not Working"
          }`,
        ].filter((detail) => detail && !detail.includes("Unknown Unknown")),
      },
      identification: {
        details: [
          ...(rawData.license_plate?.is_visible
            ? [
              `License Plate: ${rawData.license_plate.number} (${rawData.license_plate.country_format})`,
              `Plate Readability: ${rawData.license_plate.readability}`,
            ]
            : ["License Plate: Not Visible"]),
          ...(rawData.vin_info?.is_visible
            ? [
              `VIN: ${rawData.vin_info.number}`,
              `VIN Location: ${rawData.vin_info.location}`,
              `VIN Readability: ${rawData.vin_info.readability}`,
            ]
            : ["VIN: Not Visible"]),
          ...(rawData.vehicle_readings?.odometer_visible
            ? [`Odometer: ${rawData.vehicle_readings.odometer_reading}`]
            : []),
        ],
      },
      environment_quality: {
        details: [
          `Environment: ${rawData.background_details?.environment || "Unknown"
          } - ${rawData.background_details?.location_type || "Unknown"}`,
          `Time: ${rawData.background_details?.time_of_day || "Unknown"
          }, Weather: ${rawData.background_details?.weather || "Unknown"}`,
          `Exposure: ${rawData.exposure_info?.description || "Unknown"
          } (Level: ${rawData.exposure_info?.level || "N/A"})`,
          ...(
            rawData.background_details?.notable_elements?.slice(0, 2) || []
          ).map((element) => `Notable: ${element}`),
        ],
      },
      authenticity_check: {
        status: rawData.is_real_image ? "pass" : "fail",
        details: [
          `Real Image: ${rawData.is_real_image ? "Yes" : "No"}`,
          `Reasoning: ${rawData.reasoning || "No reasoning provided"}`,
          `Vehicle Consistency: ${rawData.vehicle_consistency?.same_vehicle_across_images
            ? "Consistent"
            : "Inconsistent"
          }`,
          `Confidence Level: ${rawData.vehicle_consistency?.confidence_level || "Unknown"
          }`,
        ],
      },
      image_quality: {
        status:
          rawData.image_quality?.quality_issues?.length === 0 &&
            !rawData.image_quality?.has_blur &&
            !rawData.image_quality?.low_light
            ? "pass"
            : "fail",
        details: [
          `Blur Detected: ${rawData.image_quality?.has_blur ? "Yes" : "No"}`,
          `Low Light: ${rawData.image_quality?.low_light ? "Yes" : "No"}`,
          `IoI Pattern: ${rawData.image_quality?.has_ioi_pattern ? "Yes" : "No"
          }`,
          `Quality Issues: ${rawData.image_quality?.quality_issues?.length
            ? rawData.image_quality.quality_issues.join(", ")
            : "None"
          }`,
        ],
      },
      vehicle_condition: {
        status:
          !rawData.is_dirty && !rawData.has_obstructions ? "pass" : "warning",
        details: [
          `Cleanliness: ${rawData.cleanliness_info?.status || "Unknown"}`,
          `Dirty: ${rawData.is_dirty ? "Yes" : "No"}`,
          `Obstructions: ${rawData.has_obstructions ? "Yes" : "No"}`,
          `Reflective Surface: ${rawData.is_reflective ? "Yes" : "No"}`,
          ...(
            rawData.modifications_detected
              ?.filter((mod) => mod !== "none")
              .slice(0, 2) || []
          ).map((mod) => `Modification: ${mod}`),
        ],
      },
    };
  };

  // Get vehicle image from uploads object (prioritizing front-left → front-right → any side)
  const getVehicleImageFromAPI = (assessment) => {
    // Check if uploads exist
    if (!assessment?.uploads || assessment.uploads.length === 0) {
      return placeholderFor(getClientEntry(getSourceName())?.inspectionLine);
    }

    // Helper function to get image URL from upload object
    const getImageUrl = (upload) => {
      const imagePath = upload.upload_picture || upload.picture;
      return imagePath ? getMediaUrl(imagePath) : null;
    };

    // Define priority order for vehicle sides
    const priorityOrder = [
      "frontLeft",
      "frontRight",
      "front",
      "left",
      "right",
      "rear",
      "rearLeft",
      "rearRight",
    ];

    // First, try to find images by priority order
    for (const side of priorityOrder) {
      const upload = assessment.uploads.find(
        (upload) => upload.side?.toLowerCase() === side.toLowerCase(),
      );
      if (upload) {
        const imageUrl = getImageUrl(upload);
        if (imageUrl) {
          return imageUrl;
        }
      }
    }

    // If no specific side found, get any upload with a valid image
    for (const upload of assessment.uploads) {
      const imageUrl = getImageUrl(upload);
      if (imageUrl) {
        return imageUrl;
      }
    }

    // No photograph on the case means no photograph shown — a stock car here
    // reads as this claim's vehicle.
    return placeholderFor(getClientEntry(getSourceName())?.inspectionLine);
  };

  // Call APIs when component mounts and caseId is available
  useEffect(() => {
    if (caseId) {
      // Call both APIs
      callAssessmentAPI(caseId);
      callAISummaryAPI(caseId);
    }
  }, [caseId]);

  // Point of Impact / Digital Passport are client-gated, so fetch them on first
  // open rather than on mount — clients without the tabs never issue the call.
  useEffect(() => {
    if (!caseId) return;
    if (tab === "poi" && !poiData && !isPoiLoading) {
      callPoiAPI(caseId);
    }
    if (tab === "passport" && !passportData && !isPassportLoading) {
      callDigitalPassportAPI(caseId);
    }
  }, [tab, caseId]);

  useEffect(() => {
    if (tab === "documents" && !isDocumentsTabEnabled) {
      setTab(
        pageConfig.general.defaultTab === "documents"
          ? "ai"
          : pageConfig.general.defaultTab
      );
    }
  }, [tab, isDocumentsTabEnabled]);

  // Keywords that mark an upload as an identifier (VIN, ODO, dashboard, etc.)
  const IDENTIFIER_KEYWORDS = [
    "vin", "odometer", "odo", "dashboard", "windshield",
    "rc", "registration", "license", "number_plate", "chassis",
    "instrument", "meter", "document",
  ];

  // Normalized car-side labels that mark an upload as an exterior shot
  const EXTERIOR_SIDES = new Set([
    "rear", "front", "left", "right", "back", "top", "side",
    "rear_left", "rear_right", "front_left", "front_right",
    "back_right", "back_left", "left_rear", "right_front",
    "rearleft", "rearright", "frontleft", "frontright",
  ]);

  // New API encodes side as prefix before "-CODE_<id>" e.g. "FRONT-LEFT-CODE_382454984"
  const extractSideFromName = (name) =>
    (name || "").replace(/-CODE_\d+$/i, "").toLowerCase().replace(/-/g, "_").replace(/[^a-z_]/g, "");

  const uploadIsIdentifier = (upload) => {
    const fields = [upload.name, upload.part_name, upload.side, upload.type]
      .filter(Boolean)
      .map((f) => f.toLowerCase());
    return fields.some((f) => IDENTIFIER_KEYWORDS.some((kw) => f.includes(kw)));
  };

  const uploadIsExterior = (upload) => {
    if (upload.name === "car_image") return true;
    // Check side parsed from the -CODE_ name format first, then fallback fields
    const candidates = [
      extractSideFromName(upload.name),
      (upload.side || "").toLowerCase().replace(/[\s-]/g, "_").replace(/[^a-z_]/g, ""),
      (upload.part_name || "").toLowerCase().replace(/[\s-]/g, "_").replace(/[^a-z_]/g, ""),
    ].filter(Boolean);
    return candidates.some((c) => EXTERIOR_SIDES.has(c));
  };

  const categorizeUploads = () => {
    if (!assessmentData || !assessmentData.uploads) {
      return { exteriors: [], closeups: [], identifiers: [] };
    }

    const categories = {
      exteriors: [],
      closeups: [],
      identifiers: [],
    };
    // The classification below always lands a row in exactly one bucket, so
    // it can't itself produce a duplicate. A photo rendered twice under
    // Exterior/Closeup means assessmentData.uploads carried the same row
    // twice (id/picture both repeated) — seen here rather than asking every
    // caller of categorizeUploads to dedupe it separately.
    const seenUploads = new Set();

    assessmentData.uploads.forEach((upload) => {
      const uploadKey = upload?.id ?? upload?.upload_picture ?? upload?.picture ?? null;
      if (uploadKey !== null) {
        if (seenUploads.has(uploadKey)) return;
        seenUploads.add(uploadKey);
      }

      const sideLabel = upload.side || upload.part_name ||
        extractSideFromName(upload.name).replace(/_/g, " ").toUpperCase();
      const imageData = {
        id: upload.id,
        name: upload.name,
        title: sideLabel,
        side: sideLabel,
        imageUrl: getMediaUrl(upload.upload_picture || upload.picture),
        fileName: upload.file_name || "Unknown",
      };

      // Identifiers checked first — a windshield may also have a "front" side label
      if (uploadIsIdentifier(upload)) {
        categories.identifiers.push(imageData);
      } else if (uploadIsExterior(upload)) {
        categories.exteriors.push(imageData);
      } else {
        categories.closeups.push(imageData);
      }
    });

    return categories;
  };

  const getDamageGalleryImages = () => {
    if (!assessmentData) return [];

    const damageImages =
      normalizeArray(assessmentData.all_damages)
        .filter((damage) => getProcessedDamageImagePath(damage))
        .map((damage) => ({
          id: damage.id,
          side: damage.side,
          imageUrl: getMediaUrl(getProcessedDamageImagePath(damage)),
          originalImageUrl: getMediaUrl(getOriginalDamageImagePath(damage)),
          title: damage.side,
          source: "all_damages",
        })) || [];

    const assessmentDamageImages = normalizeArray(
      assessmentData.assessment_details,
    ).flatMap((part, partIndex) =>
      normalizeArray(part.damage_images)
        .filter((image) => getProcessedDamageImagePath(image, true))
        .map((image, imageIndex) => ({
          id:
            image.id ||
            `${part.id || part.part_name || "damage"}-${partIndex}-${imageIndex}`,
          side: image.side || part.side,
          imageUrl: getMediaUrl(getProcessedDamageImagePath(image, true)),
          originalImageUrl: getMediaUrl(getOriginalDamageImagePath(image, true)),
          title: part.part_name || image.side || part.side,
          source: "damage_images",
        })),
    );

    return getUniqueImages([...damageImages, ...assessmentDamageImages]);
  };

  const getInventoryOfDamagesImages = () => {
    if (!assessmentData) return [];

    const damageImages =
      normalizeArray(assessmentData.all_damages)
        .filter((damage) => getProcessedDamageImagePath(damage, false))
        .map((damage) => {
          const rawPath = getProcessedDamageImagePath(damage, false);
          const url = getMediaUrl(rawPath);

          return {
            id: damage.id,
            side: damage.side,
            imageUrl: url,
            originalImageUrl: getMediaUrl(getOriginalDamageImagePath(damage, true)),
            title: damage.side,
            source: "all_damages",
          };
        }) || [];

    return getUniqueImages(damageImages);
  };

  const getPartWiseDamageImages = () => {
    if (!assessmentData) return [];

    const partImages = normalizeArray(assessmentData.assessment_details).flatMap(
      (part, partIndex) => {
        const damageImagesList = part.damage_images || part.damageImages;
        const images = normalizeArray(damageImagesList);

        return images
          .filter((image) => getProcessedDamageImagePath(image, false))
          .map((image, imageIndex) => {
            const rawPath = getProcessedDamageImagePath(image, false);
            const url = getMediaUrl(rawPath);

            return {
              id:
                image.id ||
                `${part.id || part.part_name || "part-damage"}-${partIndex}-${imageIndex}`,
              side: image.side || part.side,
              imageUrl: url,
              originalImageUrl: getMediaUrl(getOriginalDamageImagePath(image, true)),
              title: part.part_name || image.side || part.side,
              source: "damage_images",
            };
          });
      }
    );

    return getUniqueImages(partImages);
  };

  // Helper function to open lightbox with specific image set
  const openLightbox = (images, startIndex = 0, isDamageImages = false) => {
    setLightboxImages(images);
    setLightboxCurrentIndex(startIndex);
    setLightboxIsDamageImages(isDamageImages);
    setLightboxOpen(true);
  };

  // Helper function to close lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImages([]);
    setLightboxCurrentIndex(0);
    setLightboxIsDamageImages(false);
  };

  // Helper function to get all images for lightbox
  const getAllImagesForLightbox = () => {
    const allImages = [];

    // Add damage images from API
    allImages.push(...getDamageGalleryImages());

    // Add upload images
    const categories = categorizeUploads();
    const labelledUploads = [
      ...categories.exteriors.map((u) => ({ ...u, category: "Exterior" })),
      ...categories.closeups.map((u) => ({ ...u, category: "Close-up" })),
      ...categories.identifiers.map((u) => ({ ...u, category: "Identifier" })),
    ];
    labelledUploads.forEach((upload) => {
      allImages.push({
        id: upload.id,
        imageUrl: upload.imageUrl,
        title: upload.title || upload.category,
        side: upload.side || upload.name,
        source: "uploads",
      });
    });

    return allImages;
  };

  const vehicleViews = [
    { name: "FRONT", component: CarStencilFront },
    { name: "RIGHT SIDE", component: CarStencilLeft },
    { name: "REAR", component: CarStencilBack },
    { name: "LEFT SIDE", component: CarStencilRight },
  ];

  const nextView = () => {
    setCurrentVehicleView((prev) => (prev + 1) % vehicleViews.length);
  };

  const prevView = () => {
    setCurrentVehicleView(
      (prev) => (prev - 1 + vehicleViews.length) % vehicleViews.length,
    );
  };

  const getDocumentsCacheKey = (refNum) =>
    `https://documents-cache.local/${encodeURIComponent(refNum)}/master-data`;

  const getDocumentsMetaKey = (refNum) => `documents-tab-cache-meta:${refNum}`;

  const readZipText = (bytes) => {
    try {
      return new TextDecoder().decode(bytes);
    } catch (error) {
      let result = "";
      bytes.forEach((byte) => {
        result += String.fromCharCode(byte);
      });
      return result;
    }
  };

  const inflateZipEntry = async (compressedBytes, compressionMethod) => {
    if (compressionMethod === 0) {
      return compressedBytes.buffer.slice(
        compressedBytes.byteOffset,
        compressedBytes.byteOffset + compressedBytes.byteLength
      );
    }

    if (compressionMethod === 8) {
      const decompressionStream = new DecompressionStream("deflate-raw");
      return await new Response(
        new Blob([compressedBytes]).stream().pipeThrough(decompressionStream)
      ).arrayBuffer();
    }

    throw new Error(`Unsupported ZIP compression method: ${compressionMethod}`);
  };

  const extractPdfFromZipBlob = async (zipBlob) => {
    const buffer = await zipBlob.arrayBuffer();
    const uint8 = new Uint8Array(buffer);
    const view = new DataView(buffer);

    let endOfCentralDirOffset = -1;
    const searchStart = Math.max(0, uint8.length - 65557);

    for (let i = uint8.length - 22; i >= searchStart; i -= 1) {
      if (view.getUint32(i, true) === 0x06054b50) {
        endOfCentralDirOffset = i;
        break;
      }
    }

    if (endOfCentralDirOffset === -1) {
      throw new Error("ZIP central directory not found");
    }

    const totalEntries = view.getUint16(endOfCentralDirOffset + 10, true);
    let centralDirectoryOffset = view.getUint32(
      endOfCentralDirOffset + 16,
      true
    );

    for (let entryIndex = 0; entryIndex < totalEntries; entryIndex += 1) {
      if (view.getUint32(centralDirectoryOffset, true) !== 0x02014b50) {
        throw new Error("Invalid ZIP central directory entry");
      }

      const compressionMethod = view.getUint16(
        centralDirectoryOffset + 10,
        true
      );
      const compressedSize = view.getUint32(centralDirectoryOffset + 20, true);
      const fileNameLength = view.getUint16(centralDirectoryOffset + 28, true);
      const extraLength = view.getUint16(centralDirectoryOffset + 30, true);
      const commentLength = view.getUint16(centralDirectoryOffset + 32, true);
      const localHeaderOffset = view.getUint32(
        centralDirectoryOffset + 42,
        true
      );

      const fileNameBytes = uint8.slice(
        centralDirectoryOffset + 46,
        centralDirectoryOffset + 46 + fileNameLength
      );
      const fileName = readZipText(fileNameBytes);

      centralDirectoryOffset += 46 + fileNameLength + extraLength + commentLength;

      if (!fileName.toLowerCase().endsWith(".pdf")) {
        continue;
      }

      if (view.getUint32(localHeaderOffset, true) !== 0x04034b50) {
        throw new Error("Invalid ZIP local file header");
      }

      const localFileNameLength = view.getUint16(localHeaderOffset + 26, true);
      const localExtraLength = view.getUint16(localHeaderOffset + 28, true);
      const fileDataStart =
        localHeaderOffset + 30 + localFileNameLength + localExtraLength;
      const compressedBytes = uint8.slice(
        fileDataStart,
        fileDataStart + compressedSize
      );
      const pdfBuffer = await inflateZipEntry(
        compressedBytes,
        compressionMethod
      );

      return {
        blob: new Blob([pdfBuffer], { type: "application/pdf" }),
        fileName,
      };
    }

    throw new Error("No PDF file found in ZIP archive");
  };

  const getCachedDocumentsArchive = async (refNum) => {
    if (!("caches" in window)) {
      return null;
    }

    const metaValue = window.localStorage.getItem(getDocumentsMetaKey(refNum));
    if (!metaValue) {
      return null;
    }

    try {
      const meta = JSON.parse(metaValue);
      if (!meta.cachedAt || Date.now() - meta.cachedAt > DOCUMENTS_CACHE_TTL_MS) {
        window.localStorage.removeItem(getDocumentsMetaKey(refNum));
        const cache = await window.caches.open(DOCUMENTS_CACHE_NAME);
        await cache.delete(getDocumentsCacheKey(refNum));
        return null;
      }

      const cache = await window.caches.open(DOCUMENTS_CACHE_NAME);
      const response = await cache.match(getDocumentsCacheKey(refNum));
      if (!response) {
        return null;
      }

      const displayClaimId = assessmentData?.info?.num_plate?.trim() ? assessmentData.info.num_plate.trim() : caseId;
      return {
        blob: await response.blob(),
        fileName: `${displayClaimId}.zip`,
      };
    } catch (error) {
      console.error("Error reading documents cache:", error);
      return null;
    }
  };

  const cacheDocumentsArchive = async (refNum, blob, fileName) => {
    if (!("caches" in window)) {
      return;
    }

    const cache = await window.caches.open(DOCUMENTS_CACHE_NAME);
    await cache.put(
      getDocumentsCacheKey(refNum),
      new Response(blob, {
        headers: {
          "Content-Type": blob.type || "application/zip",
        },
      })
    );

    window.localStorage.setItem(
      getDocumentsMetaKey(refNum),
      JSON.stringify({
        cachedAt: Date.now(),
        fileName,
      })
    );
  };

  const fetchDocumentsArchive = async (forceRefresh = false) => {
    const refNum = assessmentData?.info?.ref_num || caseId;
    const sourceName = apiSourceName;

    if (!refNum) {
      throw new Error("Reference number not found");
    }

    if (!forceRefresh) {
      const cachedArchive = await getCachedDocumentsArchive(refNum);
      if (cachedArchive) {
        return {
          ...cachedArchive,
          refNum,
        };
      }
    }

    const formData = new FormData();
    formData.append("ref_num", refNum);
    formData.append("source_name", sourceName);

    const token = getAuthToken();
    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await fetch(`${getApiBaseUrl()}/download_master_data`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "*/*",
        Referer: withTrailingSlash(PORTAL_BASE_URL),
        "User-Agent":
          "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/143.0.0.0 Safari/537.36",
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.url) {
      throw new Error("Download URL not found in response");
    }

    const fileResponse = await fetch(data.url);
    if (!fileResponse.ok) {
      throw new Error(`Failed to download file: ${fileResponse.status}`);
    }

    const blob = await fileResponse.blob();
    const displayClaimId = assessmentData?.info?.num_plate?.trim() ? assessmentData.info.num_plate.trim() : caseId;
    const fileName = `${displayClaimId}.zip`;
    await cacheDocumentsArchive(refNum, blob, fileName);

    return {
      blob,
      refNum,
      fileName,
    };
  };

  useEffect(() => {
    return () => {
      if (documentsPreviewUrl) {
        window.URL.revokeObjectURL(documentsPreviewUrl);
      }
    };
  }, [documentsPreviewUrl]);

  useEffect(() => {
    setDocumentsArchiveName("");
    setDocumentsPreviewError("");
    setDocumentsPreviewUrl((prevUrl) => {
      if (prevUrl) {
        window.URL.revokeObjectURL(prevUrl);
      }
      return "";
    });
  }, [caseId]);

  useEffect(() => {
    const shouldLoadDocumentsPreview =
      tab === "documents" &&
      isDocumentsTabEnabled &&
      pageConfig.tabs.documents.enabled &&
      pageConfig.tabs.documents.documentTypes.pdfReport.enabled;

    if (!shouldLoadDocumentsPreview) {
      return;
    }

    let isCancelled = false;

    const loadDocumentsPreview = async () => {
      setIsDocumentsLoading(true);
      setDocumentsPreviewError("");

      try {
        const archive = await fetchDocumentsArchive(false);
        const pdfAsset = await extractPdfFromZipBlob(archive.blob);
        if (isCancelled) {
          return;
        }

        setDocumentsArchiveName(archive.fileName);
        setDocumentsPreviewUrl((prevUrl) => {
          if (prevUrl) {
            window.URL.revokeObjectURL(prevUrl);
          }
          return window.URL.createObjectURL(pdfAsset.blob);
        });
      } catch (error) {
        if (isCancelled) {
          return;
        }

        console.error("Error loading documents preview:", error);
        setDocumentsPreviewError(
          "Preview unavailable. You can still use Download Images and Report."
        );
        setDocumentsPreviewUrl((prevUrl) => {
          if (prevUrl) {
            window.URL.revokeObjectURL(prevUrl);
          }
          return "";
        });
      } finally {
        if (!isCancelled) {
          setIsDocumentsLoading(false);
        }
      }
    };

    loadDocumentsPreview();

    return () => {
      isCancelled = true;
    };
  }, [tab, caseId, assessmentData]);

  // Function to download PDF report
  const downloadPDFReport = async () => {
    if (isReportDownloading) return;

    setIsReportDownloading(true);
    try {
      const archive = await fetchDocumentsArchive(false);
      const url = window.URL.createObjectURL(archive.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = archive.fileName;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF report. Please try again.");
    } finally {
      setIsReportDownloading(false);
    }
  };

  return (
    <div
      className="bg-background-primary w-full h-screen flex flex-col relative overflow-hidden"
      data-name="Underwriting"
    >
      <OnboardingTour
        tourKey="caseDetails"
        steps={TOUR_STEPS.caseDetails}
        ready={!isLoading && !!assessmentData}
      />
      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50">
          <div className="text-center">
            <img
              src={loadingIcon}
              alt="Loading"
              className="w-8 h-8 animate-spin mx-auto mb-4"
            />
            <p className="text-gray-600">Loading assessment data...</p>
          </div>
        </div>
      )}

      {/* Top header bar - unified AI assessed component with warning */}
      <div className="bg-background-secondary border-b border-[rgba(148,158,173,0.25)] min-h-[80px] flex-shrink-0 p-3">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          {/* Combined AI assessed container with car thumbnail, case info, and warning */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center">
              {/* Car thumbnail */}
              <div className="w-[70px] lg:w-[100px] h-[40px] lg:h-[56px] rounded-bl-[6px] rounded-tl-[6px] overflow-hidden flex-shrink-0">
                <img
                  alt="Car thumbnail"
                  className="w-full h-full object-cover"
                  src={getVehicleImageFromAPI(assessmentData)}
                />
              </div>

              {/* Case info section with border */}
              <div className="flex flex-col lg:flex-row lg:items-center border border-border-primary rounded-br-[6px] rounded-tr-[6px] lg:h-[56px] min-h-[40px]">
                {/* Status info */}
                <div className="flex flex-col justify-center gap-0.5 px-3 border-b lg:border-b-0 lg:border-r border-border-primary py-1.5 lg:py-0">
                  {(() => {
                    // Pass the raw value straight through: getStatusInfo now
                    // reports Unknown for a missing state. The previous fallback
                    // to getStatusInfo(3) labelled every stateless case
                    // "AI Assessed", and the truthiness check also mislabelled a
                    // genuine claim_state of 0.
                    const statusInfo = getStatusInfo(
                      parseInt(assessmentData?.info?.claim_state),
                    );

                    return (
                      <div className="flex gap-[8px] items-center">
                        <div className="overflow-clip relative shrink-0 size-[24px]">
                          <img
                            alt={statusInfo.text}
                            className="block max-w-none size-full"
                            src={statusInfo.icon}
                          />
                        </div>
                        <div
                          className="font-['Noto_Sans:Medium',_sans-serif] font-medium leading-[0] text-sm text-nowrap tracking-[-0.28px]"
                          style={{
                            fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                            color: statusInfo.color,
                          }}
                        >
                          <p className="leading-[20px] whitespace-pre">
                            {statusInfo.text}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                  <div
                    className="font-medium text-text-primary text-sm lg:text-lg tracking-[-0.18px]"
                    style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                  >
                    {assessmentData?.info?.num_plate?.trim()
                      ? assessmentData.info.num_plate.trim()
                      : caseId}
                  </div>
                </div>

                {/* Warning section - part of the same component */}
                <button className="flex items-center gap-1.5 px-3 py-1.5 lg:py-2 hover:bg-gray-50 transition-colors">
                  <CaptureIssues
                    assessment={assessmentData}
                    isLoading={isLoading}
                  />
                </button>
              </div>

              <div className="hidden xl:flex items-center gap-2 ml-3">
                {headerVehicleDetails.map((detail) => (
                  <div
                    key={detail.label}
                    className="bg-white border border-border-primary rounded-[8px] px-3 py-2 min-w-[96px]"
                  >
                    <div
                      className="font-medium text-text-secondary text-[10px] uppercase tracking-[-0.1px] leading-none mb-1"
                      style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                    >
                      {detail.label}
                    </div>
                    <div
                      className="font-semibold text-text-primary text-sm tracking-[-0.14px] leading-[18px] truncate max-w-[130px]"
                      style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                      title={detail.value || "N/A"}
                    >
                      {detail.value || "N/A"}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 xl:hidden">
              {headerVehicleDetails.map((detail) => (
                <div
                  key={detail.label}
                  className="bg-white border border-border-primary rounded-[8px] px-3 py-2 min-w-0"
                >
                  <div
                    className="font-medium text-text-secondary text-[10px] uppercase tracking-[-0.1px] leading-none mb-1"
                    style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                  >
                    {detail.label}
                  </div>
                  <div
                    className="font-semibold text-text-primary text-sm tracking-[-0.14px] leading-[18px] truncate"
                    style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                    title={detail.value || "N/A"}
                  >
                    {detail.value || "N/A"}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action buttons - positioned on the right */}
          <div className="flex gap-3 flex-wrap lg:flex-nowrap justify-center lg:justify-end items-center">
            {(countryName || timeTakenToUpload) && (
              <div className="flex flex-col items-end gap-[3px] text-sm text-text-secondary">
                {countryName && (
                  <div className="flex items-center gap-1.5">
                    <span>Country: {countryName}</span>
                    {countryFlagSrc && (
                      <img
                        alt={countryName}
                        className="h-[14px] w-5 rounded-[2px] object-cover"
                        src={countryFlagSrc}
                      />
                    )}
                  </div>
                )}
                {timeTakenToUpload && (
                  <div>Time taken to upload: {formatUploadDuration(timeTakenToUpload)}</div>
                )}
              </div>
            )}
            {pageConfig.header.showApproveButton &&
              assessmentData?.info?.claim_state &&
              (parseInt(assessmentData.info.claim_state) === 3 ||
                parseInt(assessmentData.info.claim_state) === 5) && (
                <button
                  type="button"
                  onClick={handleApproveClick}
                  data-tour="case-approve-button"
                  className="bg-status-success flex gap-[4px] items-center justify-center px-2.5 lg:px-3 py-1.5 lg:py-2 rounded-[24px] hover:opacity-90 transition-colors flex-shrink-0"
                >

                  <span
                    className="font-semibold text-xs text-white lg:text-sm tracking-[-0.14px] whitespace-nowrap"
                    style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                  >
                    {pageConfig.header.approveButtonLabel}
                  </span>
                </button>
              )}
            {/* <button className="bg-[#df3822] flex gap-[4px] items-center justify-center px-2.5 lg:px-3 py-1.5 lg:py-2 rounded-[24px] hover:bg-[#c73019] transition-colors">
              <div className="overflow-clip size-3.5 lg:size-4">
                <img
                  alt=""
                  className="block max-w-none size-full"
                  src={cameraIcon}
                />
              </div>
              <span
                className="font-semibold text-xs lg:text-sm text-white tracking-[-0.14px]"
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                RECAPTURE
              </span>
            </button> */}
            {/* <button className="bg-[#df3822] flex gap-[4px] items-center justify-center px-2.5 lg:px-3 py-1.5 lg:py-2 rounded-[24px] hover:bg-[#c73019] transition-colors">
              <div className="flex items-center justify-center">
                <div className="rotate-180">
                  <div className="overflow-clip size-3.5 lg:size-4">
                    <img
                      alt=""
                      className="block max-w-none size-full"
                      src={aiAssessedIcon}
                    />
                  </div>
                </div>
              </div>
              <span
                className="font-semibold text-white text-xs lg:text-sm tracking-[-0.14px]"
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                REJECT
              </span>
            </button> */}
          </div>
        </div>
      </div>

      <div
        className={`bg-background-secondary flex-shrink-0 overflow-hidden transition-[max-height,padding,opacity] duration-1000 ease-in-out ${shouldCollapseVehicleStencil
            ? "max-h-0 px-4 lg:px-6 py-0 opacity-0"
            : "max-h-[40vh] p-4 lg:p-6 opacity-100"
          }`}
      >
        <div
          className={`relative h-[40vh] overflow-hidden transition-transform duration-1000 ease-in-out ${shouldCollapseVehicleStencil
              ? "-translate-y-2 scale-[0.98]"
              : "translate-y-0 scale-100"
            }`}
        >
          {/* Navigation buttons */}
          {/* <button
              onClick={prevView}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white border border-gray-200 rounded-full p-1 shadow-sm transition-all duration-200 hover:shadow-md"
            >
              <ChevronLeft className="h-3 w-3 text-gray-600" />
            </button>

            <button
              onClick={nextView}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white border border-gray-200 rounded-full p-1 shadow-sm transition-all duration-200 hover:shadow-md"
            >
              <ChevronRight className="h-3 w-3 text-gray-600" />
            </button> */}

          {/* Vehicle diagram with damage indicators */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-full h-full flex items-center justify-center">
              <CarDamageAssessment
                isLoading={isLoading}
                assessmentData={assessmentData}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs section - centered */}
      <div className="bg-background-secondary border-b border-[rgba(148,158,173,0.25)] px-1 lg:px-2 flex-shrink-0">
        <div className="flex justify-center py-4">
          <div className="flex flex-wrap justify-center lg:flex-nowrap gap-3 lg:gap-6 max-w-4xl">
            {pageConfig.tabs.aiSummary.enabled && (
              <button
                className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors shadow-sm ${tab === "ai"
                    ? "bg-primary text-white"
                    : "bg-white text-text-secondary hover:text-text-primary border border-gray-200"
                  }`}
                onClick={() => setTab("ai")}
                data-tour="case-tab-ai"
              >
                <div className="overflow-clip size-4">
                  <img
                    alt=""
                    className={`block max-w-none size-full ${tab === "ai" ? "" : "brightness-0 opacity-60"
                      }`}
                    src={magicWandIcon}
                  />
                </div>
                <span
                  className="font-semibold text-sm tracking-[-0.16px] whitespace-nowrap"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  {pageConfig.tabs.aiSummary.label}
                </span>
              </button>
            )}
            {pageConfig.tabs.overview.enabled && (
              <button
                className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors shadow-sm ${tab === "overview"
                    ? "bg-primary text-white"
                    : "bg-white text-text-secondary hover:text-text-primary border border-gray-200"
                  }`}
                onClick={() => setTab("overview")}
                data-tour="case-tab-overview"
              >
                <div className="overflow-clip size-4">
                  <img
                    alt=""
                    className="block max-w-none size-full"
                    src={glassesIcon}
                  />
                </div>
                <span
                  className="font-semibold text-sm tracking-[-0.16px] whitespace-nowrap"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  {pageConfig.tabs.overview.label}
                </span>
              </button>
            )}
            {pageConfig.tabs.images.enabled && (
              <button
                className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors shadow-sm ${tab === "images"
                    ? "bg-primary text-white"
                    : "bg-white text-text-secondary hover:text-text-primary border border-gray-200"
                  }`}
                onClick={() => setTab("images")}
                data-tour="case-tab-images"
              >
                <div className="overflow-clip size-4">
                  <img
                    alt=""
                    className="block max-w-none size-full"
                    src={imageIcon}
                  />
                </div>
                <span
                  className="font-semibold text-sm tracking-[-0.16px] whitespace-nowrap"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  {pageConfig.tabs.images.label}
                </span>
              </button>
            )}
            {pageConfig.tabs.documents.enabled && (
              <button
                type="button"
                disabled={!isDocumentsTabEnabled}
                aria-disabled={!isDocumentsTabEnabled}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors shadow-sm ${!isDocumentsTabEnabled
                    ? "bg-white text-text-secondary border border-gray-200 opacity-50 cursor-not-allowed"
                    : tab === "documents"
                      ? "bg-primary text-white"
                      : "bg-white text-text-secondary hover:text-text-primary border border-gray-200"
                  }`}
                onClick={() => {
                  if (isDocumentsTabEnabled) {
                    setTab("documents");
                  }
                }}
                data-tour="case-tab-documents"
              >
                <div className="overflow-clip size-4">
                  <img
                    alt=""
                    className="block max-w-none size-full"
                    src={folderIcon}
                  />
                </div>
                <span
                  className="font-semibold text-sm tracking-[-0.16px] whitespace-nowrap"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  {pageConfig.tabs.documents.label}
                </span>
              </button>
            )}
            {pageConfig.tabs.poi?.enabled && !caseFailed && (
              <button
                className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors shadow-sm ${tab === "poi"
                    ? "bg-primary text-white"
                    : "bg-white text-text-secondary hover:text-text-primary border border-gray-200"
                  }`}
                onClick={() => setTab("poi")}
                data-tour="case-tab-poi"
              >
                <div className="overflow-clip size-4">
                  <img
                    alt=""
                    className="block max-w-none size-full"
                    src={warningTriangleIcon}
                  />
                </div>
                <span
                  className="font-semibold text-sm tracking-[-0.16px] whitespace-nowrap"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  {pageConfig.tabs.poi.label}
                </span>
              </button>
            )}
            {pageConfig.tabs.digitalPassport?.enabled && !caseFailed && (
              <button
                className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors shadow-sm ${tab === "passport"
                    ? "bg-primary text-white"
                    : "bg-white text-text-secondary hover:text-text-primary border border-gray-200"
                  }`}
                onClick={() => setTab("passport")}
                data-tour="case-tab-passport"
              >
                <div className="overflow-clip size-4">
                  <img
                    alt=""
                    className="block max-w-none size-full"
                    src={documentIcon}
                  />
                </div>
                <span
                  className="font-semibold text-sm tracking-[-0.16px] whitespace-nowrap"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  {pageConfig.tabs.digitalPassport.label}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Content Container - scrollable (keeps window from scrolling) */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {/* AI Summary Cards - responsive grid */}
        {tab === "ai" && pageConfig.tabs.aiSummary.enabled && (
          <div className="p-1 lg:p-2 pb-4 lg:pb-6">
            {isAiSummaryLoading ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <img
                    src={loadingIcon}
                    alt="Loading"
                    className="w-8 h-8 animate-spin mx-auto mb-4"
                  />
                  <p className="text-gray-600">Loading AI summary...</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-1 lg:gap-2">
                {/* Vehicle Overview Card */}
                {pageConfig.tabs.aiSummary.cards.vehicleOverview.enabled && (
                  <div className="bg-background-secondary p-2 lg:p-3 rounded-[6px] shadow-sm">
                    <div className="mb-1">
                      <h3
                        className="font-medium text-text-primary text-xs tracking-[-0.12px] uppercase"
                        style={{
                          fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                        }}
                      >
                        {pageConfig.tabs.aiSummary.cards.vehicleOverview.title}
                      </h3>
                    </div>
                    <div className="space-y-1">
                      <div
                        className="font-normal text-text-primary text-xs lg:text-xs tracking-[-0.18px]"
                        style={{
                          fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                        }}
                      >
                        {aiSummaryData?.vehicle_overview?.details ? (
                          <ul className="space-y-1">
                            {aiSummaryData.vehicle_overview.details.map(
                              (detail, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="text-text-primary mr-1">
                                    •
                                  </span>
                                  <span>{detail}</span>
                                </li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <div className="text-text-tertiary text-xs">
                            No vehicle overview data available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Identification Card */}
                {pageConfig.tabs.aiSummary.cards.identification.enabled && (
                  <div className="bg-background-secondary p-2 lg:p-3 rounded-[6px] shadow-sm">
                    <div className="mb-1">
                      <h3
                        className="font-medium text-text-primary text-xs tracking-[-0.12px] uppercase"
                        style={{
                          fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                        }}
                      >
                        IDENTIFICATION
                      </h3>
                    </div>
                    <div className="space-y-1">
                      <div
                        className="font-normal text-text-primary text-xs lg:text-xs tracking-[-0.18px]"
                        style={{
                          fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                        }}
                      >
                        {aiSummaryData?.identification?.details ? (
                          <ul className="space-y-1">
                            {aiSummaryData.identification.details.map(
                              (detail, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="text-text-primary mr-1">
                                    •
                                  </span>
                                  <span>{detail || "-"}</span>
                                </li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <div className="text-text-tertiary text-xs">
                            No identification data available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Environment & Quality Card */}
                {pageConfig.tabs.aiSummary.cards.environmentQuality.enabled && (
                  <div className="bg-background-secondary p-2 lg:p-3 rounded-[6px] shadow-sm">
                    <div className="mb-1">
                      <h3
                        className="font-medium text-text-primary text-xs tracking-[-0.12px] uppercase"
                        style={{
                          fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                        }}
                      >
                        ENVIRONMENT & QUALITY
                      </h3>
                    </div>
                    <div className="space-y-1">
                      <div
                        className="font-normal text-text-primary text-xs lg:text-xs tracking-[-0.18px]"
                        style={{
                          fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                        }}
                      >
                        {aiSummaryData?.environment_quality?.details ? (
                          <ul className="space-y-1">
                            {aiSummaryData.environment_quality.details.map(
                              (detail, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="text-text-primary mr-1">
                                    •
                                  </span>
                                  <span>{detail}</span>
                                </li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <div className="text-text-tertiary text-xs">
                            No environment & quality data available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Authenticity Check Card */}
                {pageConfig.tabs.aiSummary.cards.authenticityCheck.enabled && (
                  <div className="bg-background-secondary p-2 lg:p-3 rounded-[6px] shadow-sm">
                    <div className="mb-1">
                      <div className="flex items-center justify-between">
                        <h3
                          className="font-medium text-text-primary text-xs tracking-[-0.12px] uppercase"
                          style={{
                            fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                          }}
                        >
                          AUTHENTICITY CHECK
                        </h3>
                        {aiSummaryData?.authenticity_check?.status && (
                          <div
                            className={`w-2 h-2 rounded-full ${aiSummaryData.authenticity_check.status === "pass"
                                ? "bg-green-500"
                                : aiSummaryData.authenticity_check.status ===
                                  "fail"
                                  ? "bg-red-500"
                                  : "bg-yellow-500"
                              }`}
                          ></div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div
                        className="font-normal text-text-primary text-xs lg:text-xs tracking-[-0.18px]"
                        style={{
                          fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                        }}
                      >
                        {aiSummaryData?.authenticity_check?.details ? (
                          <ul className="space-y-1">
                            {aiSummaryData.authenticity_check.details.map(
                              (detail, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="text-text-primary mr-1">
                                    •
                                  </span>
                                  <span>{detail}</span>
                                </li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <div className="text-text-tertiary text-xs">
                            No authenticity check data available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Image Quality Card */}
                {pageConfig.tabs.aiSummary.cards.imageQuality.enabled && (
                  <div className="bg-background-secondary p-2 lg:p-3 rounded-[6px] shadow-sm">
                    <div className="mb-1">
                      <div className="flex items-center justify-between">
                        <h3
                          className="font-medium text-text-primary text-xs tracking-[-0.12px] uppercase"
                          style={{
                            fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                          }}
                        >
                          IMAGE QUALITY
                        </h3>
                        {aiSummaryData?.image_quality?.status && (
                          <div
                            className={`w-2 h-2 rounded-full ${aiSummaryData.image_quality.status === "pass"
                                ? "bg-green-500"
                                : aiSummaryData.image_quality.status === "fail"
                                  ? "bg-red-500"
                                  : "bg-yellow-500"
                              }`}
                          ></div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div
                        className="font-normal text-text-primary text-xs lg:text-xs tracking-[-0.18px]"
                        style={{
                          fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                        }}
                      >
                        {aiSummaryData?.image_quality?.details ? (
                          <ul className="space-y-1">
                            {aiSummaryData.image_quality.details.map(
                              (detail, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="text-text-primary mr-1">
                                    •
                                  </span>
                                  <span>{detail}</span>
                                </li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <div className="text-text-tertiary text-xs">
                            No image quality data available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Vehicle Condition Card */}
                {pageConfig.tabs.aiSummary.cards.vehicleCondition.enabled && (
                  <div className="bg-background-secondary p-2 lg:p-3 rounded-[6px] shadow-sm">
                    <div className="mb-1">
                      <div className="flex items-center justify-between">
                        <h3
                          className="font-medium text-text-primary text-xs tracking-[-0.12px] uppercase"
                          style={{
                            fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                          }}
                        >
                          VEHICLE CONDITION
                        </h3>
                        {aiSummaryData?.vehicle_condition?.status && (
                          <div
                            className={`w-2 h-2 rounded-full ${aiSummaryData.vehicle_condition.status === "pass"
                                ? "bg-green-500"
                                : aiSummaryData.vehicle_condition.status ===
                                  "fail"
                                  ? "bg-red-500"
                                  : "bg-yellow-500"
                              }`}
                          ></div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div
                        className="font-normal text-text-primary text-xs lg:text-xs tracking-[-0.18px]"
                        style={{
                          fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                        }}
                      >
                        {aiSummaryData?.vehicle_condition?.details ? (
                          <ul className="space-y-1">
                            {aiSummaryData.vehicle_condition.details.map(
                              (detail, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="text-text-primary mr-1">
                                    •
                                  </span>
                                  <span>{detail}</span>
                                </li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <div className="text-text-tertiary text-xs">
                            No vehicle condition data available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Overview Tab Content */}
        {tab === "overview" && pageConfig.tabs.overview.enabled && (
          <div className="px-2 sm:px-4 lg:px-6 py-0 flex flex-col gap-4 lg:gap-8">
            <div className="flex flex-col gap-4 lg:gap-6 w-full">
              {/* Summary Cards Row */}
              <div className="flex flex-col lg:flex-row gap-2 lg:gap-4 items-stretch lg:items-center w-full">
                {/* Search Parts Button */}
                {pageConfig.tabs.overview.sections.searchParts.enabled && (
                  <button
                    onClick={() => {
                      setIsSearchActive(true);
                      openDamagedPartsModal();
                    }}
                    className="bg-white border border-text-primary rounded-[8px] px-4 lg:px-6 py-3 lg:py-4 flex flex-col gap-1 items-center justify-center flex-shrink-0"
                  >
                    <div
                      className="font-medium text-text-secondary text-xs text-center tracking-[-0.12px] whitespace-pre leading-normal"
                      style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                    >
                      <p className="mb-0">
                        {
                          pageConfig.tabs.overview.sections.searchParts.label.split(
                            " ",
                          )[0]
                        }
                      </p>
                      <p>
                        {
                          pageConfig.tabs.overview.sections.searchParts.label.split(
                            " ",
                          )[1]
                        }
                      </p>
                    </div>
                    <div className="size-[24px]">
                      <img
                        alt=""
                        className="block max-w-none size-full"
                        src={searchIcon}
                      />
                    </div>
                  </button>
                )}

                <div className="w-full">
                  <div className="flex flex-col xl:flex-row w-full gap-2 lg:gap-3">
                    {/* Damage Cards Row */}
                    <div className="flex gap-0.5 sm:gap-1 flex-1 overflow-x-auto">
                      {/* Damaged Parts */}
                      {pageConfig.tabs.overview.sections.damagedParts
                        .enabled && (
                          <button
                            className="bg-white rounded-l-lg px-2 sm:px-3 lg:px-4 py-3 lg:py-4 flex flex-col gap-1 items-start justify-center hover:bg-gray-50 transition-colors cursor-pointer flex-1 min-w-0"
                            onClick={() => openDamagedPartsModal()}
                          >
                            <div className="font-medium text-text-secondary text-left text-xs tracking-tight leading-tight">
                              <p className="mb-0">DAMAGED</p>
                              <p>PARTS</p>
                            </div>
                            <div className="flex items-start justify-between w-full">
                              <div className="flex gap-2 sm:gap-4 items-center">
                                <div className="font-medium text-text-primary text-lg lg:text-xl tracking-tight">
                                  {assessmentData?.summary?.damaged_parts || 0}
                                </div>
                                <ChevronRight className="w-6 h-6 text-gray-400" />
                              </div>
                            </div>
                          </button>
                        )}

                      {/* Severe Damage */}
                      {pageConfig.tabs.overview.sections.severeDamage
                        .enabled && (
                          <button
                            className="bg-white px-2 sm:px-3 lg:px-4 py-3 lg:py-4 flex flex-col gap-1 items-start justify-center cursor-pointer transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 flex-1 min-w-0"
                            onClick={() =>
                              openDamagedPartsModal("damaged", "severe")
                            }
                          >
                            <div className="font-medium text-text-secondary text-xs tracking-tight text-left leading-tight">
                              <p className="mb-0">SEVERE</p>
                              <p>DAMAGE</p>
                            </div>
                            <div className="flex gap-2 sm:gap-4 items-center">
                              <div className="flex gap-2 items-center">
                                <div className="w-2 h-2 rounded-full bg-red-500" />
                                <div className="font-medium text-text-primary text-lg lg:text-xl tracking-tight">
                                  {assessmentData?.summary?.severe_damage || 0}
                                </div>
                              </div>
                              <ChevronRight className="w-6 h-6 text-gray-400" />
                            </div>
                          </button>
                        )}

                      {/* Moderate Damage */}
                      {pageConfig.tabs.overview.sections.moderateDamage
                        .enabled && (
                          <button
                            className="bg-white px-2 sm:px-3 lg:px-4 py-3 lg:py-4 flex flex-col gap-1 items-start justify-center cursor-pointer transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 flex-1 min-w-0"
                            onClick={() =>
                              openDamagedPartsModal("damaged", "moderate")
                            }
                          >
                            <div className="font-medium text-text-secondary text-xs tracking-tight leading-tight">
                              <p className="mb-0">MODERATE</p>
                              <p>DAMAGE</p>
                            </div>
                            <div className="flex gap-2 sm:gap-4 items-center">
                              <div className="flex gap-2 items-center">
                                <div className="w-2 h-2 rounded-full bg-amber-500" />
                                <div className="font-medium text-text-primary text-lg lg:text-xl tracking-tight">
                                  {assessmentData?.summary?.moderate_damage || 0}
                                </div>
                              </div>
                              <ChevronRight className="w-6 h-6 text-gray-400" />
                            </div>
                          </button>
                        )}

                      {/* Minor Damage */}
                      {pageConfig.tabs.overview.sections.minorDamage
                        .enabled && (
                          <button
                            className="bg-white rounded-r-lg px-2 sm:px-3 lg:px-4 py-3 lg:py-4 flex flex-col gap-1 items-start justify-center cursor-pointer transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 flex-1 min-w-0"
                            onClick={() =>
                              openDamagedPartsModal("damaged", "minor")
                            }
                          >
                            <div className="font-medium text-text-secondary text-xs tracking-tight leading-tight">
                              <p className="mb-0">MINOR</p>
                              <p>DAMAGE</p>
                            </div>
                            <div className="flex items-start justify-between w-full">
                              <div className="flex gap-2 sm:gap-4 items-center">
                                <div className="flex gap-2 items-center">
                                  <div className="w-2 h-2 rounded-full bg-green-500" />
                                  <div className="font-medium text-text-primary text-lg lg:text-xl tracking-tight">
                                    {assessmentData?.summary?.minor_damage || 0}
                                  </div>
                                </div>
                                <ChevronRight className="w-6 h-6 text-gray-400" />
                              </div>
                            </div>
                          </button>
                        )}
                    </div>

                    {/* Info Cards Row */}
                    <div className="flex gap-2 lg:gap-3 flex-wrap xl:flex-nowrap">
                      {/* Parts Identified */}
                      {pageConfig.tabs.overview.sections.partsIdentified
                        .enabled && (
                          <button
                            onClick={() => setOverviewModalType("parts")}
                            className="bg-white rounded-lg px-3 lg:px-4 py-3 lg:py-4 flex flex-col gap-1 items-start justify-center hover:bg-gray-50 transition-colors flex-1 min-w-[140px]"
                          >
                            <div className="font-medium text-text-secondary text-xs tracking-tight leading-tight">
                              <p className="mb-0">PARTS</p>
                              <p>IDENTIFIED</p>
                            </div>
                            <div className="flex gap-3 items-center w-full">
                              <div className="flex gap-1 items-center">
                                <div className="size-[20px]">
                                  <img
                                    alt=""
                                    className="block max-w-none size-full"
                                    src={
                                      partsOverview.unidentified.length === 0
                                        ? checkCircleGreen
                                        : warningTriangleIcon
                                    }
                                  />
                                </div>
                                <div className="font-medium text-text-primary text-lg lg:text-xl tracking-tight whitespace-nowrap">
                                  {assessmentData?.summary?.parts_identified ??
                                    partsOverview.identified.length}
                                </div>
                              </div>
                              <ChevronRight className="w-6 h-6 text-gray-400" />
                            </div>
                          </button>
                        )}

                      {/* Sides Identified */}
                      {pageConfig.tabs.overview.sections.sidesIdentified
                        .enabled && (
                          <button
                            onClick={() => setOverviewModalType("sides")}
                            className="bg-white rounded-lg px-3 lg:px-4 py-3 lg:py-4 flex flex-col gap-1 items-start justify-center hover:bg-gray-50 transition-colors flex-1 min-w-[140px]"
                          >
                            <div className="font-medium text-text-secondary text-xs tracking-tight leading-tight">
                              <p className="mb-0">SIDES</p>
                              <p>IDENTIFIED</p>
                            </div>
                            <div className="flex gap-3 items-center w-full">
                              <div className="flex gap-1 items-center">
                                <div className="size-[20px]">
                                  <img
                                    alt=""
                                    className="block max-w-none size-full"
                                    src={
                                      sidesOverview.unidentified.length > 0
                                        ? warningTriangleIcon
                                        : checkCircleGreen
                                    }
                                  />
                                </div>
                                <div className="font-medium text-text-primary text-lg lg:text-xl tracking-tight whitespace-nowrap">
                                  {sidesOverview.identified.length}
                                </div>
                              </div>
                              <ChevronRight className="w-6 h-6 text-gray-400" />
                            </div>
                          </button>
                        )}

                      {/* Location Check — disabled when the case's own
                          warnings say the user never granted location
                          permission, since there is nothing behind it to
                          check in that case. Checked against both places
                          that can say so (see hasNoLocationPermissionWarning)
                          so this agrees with what Capture Issues, right next
                          to it, is already showing. */}
                      {pageConfig.tabs.overview.sections.locationCheck
                        .enabled && (() => {
                          const locationPermissionDenied =
                            hasNoLocationPermissionWarning(assessmentData);
                          return (
                            <button
                              onClick={() => !locationPermissionDenied && setIsOpen(true)}
                              disabled={locationPermissionDenied}
                              className={`bg-white rounded-lg px-3 lg:px-4 py-3 lg:py-4 flex flex-col gap-1 items-start justify-center transition-colors flex-1 min-w-[140px] ${
                                locationPermissionDenied
                                  ? "opacity-60 cursor-not-allowed"
                                  : "hover:bg-gray-50"
                              }`}
                            >
                              <div className="font-medium text-text-secondary text-left text-xs tracking-tight leading-tight">
                                <p className="mb-0">LOCATION</p>
                                <p>CHECK</p>
                              </div>
                              <div className="flex gap-2 items-center w-full">
                                <div className="flex gap-1 items-center">
                                  <div className="size-[20px]">
                                    <img
                                      alt=""
                                      className="block max-w-none size-full"
                                      src={locationPermissionDenied ? warningTriangleIcon : checkCircleGreen}
                                    />
                                  </div>
                                  <div className="font-medium text-text-primary text-lg lg:text-xl tracking-tight">
                                    {locationPermissionDenied ? "Not Provided" : "Passed"}
                                  </div>
                                </div>
                                {!locationPermissionDenied && (
                                  <div className="size-[24px]">
                                    <img
                                      alt=""
                                      className="block max-w-none size-full"
                                      src={arrowRightGray}
                                    />
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Information Section */}
              <div
                className="bg-background-secondary rounded-[8px] px-6 py-4 w-full"
                style={{ overflowWrap: "anywhere" }}
              >
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-5">
                  {[
                    // ref_num, not num_plate — num_plate is a license plate
                    // reading (sometimes missing it entirely; see
                    // REGISTRATION NO. below), not the case's own identifier.
                    { label: "INSPECTION ID", value: assessmentData?.info?.ref_num || caseId || "—" },
                    { label: "BRAND",          value: vehicleDetailValues.brand },
                    { label: "MODEL",          value: vehicleDetailValues.model },
                    { label: "MFG. YEAR",      value: vehicleDetailValues.mfgYear },
                    {
                      label: "CREATED ON",
                      value: assessmentData?.info?.created_at
                        ? formatDateTime(assessmentData.info.created_at)
                        : "—",
                    },
                    { label: "SERIES",         value: vehicleDetailValues.series },
                    { label: "REGISTRATION NO.", value: displayOrDash(assessmentData?.info?.num_plate) },
                    { label: "VIN",            value: displayOrDash(assessmentData?.info?.vin_reading) },
                    { label: "ODO READING",  value: assessmentData?.info?.odo_meter_reading || "—" },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex flex-col gap-[3px]">
                      <div
                        className="font-medium text-text-secondary text-xs tracking-wide uppercase leading-normal"
                        style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                      >
                        {label}
                      </div>
                      <div
                        className="font-normal text-text-primary text-sm leading-normal"
                        style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                      >
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Damaged Parts List Section */}
              {assessmentData?.summary?.damaged_parts_list &&
                assessmentData.summary.damaged_parts_list.length > 0 && (() => {
                  // Build part → severity lookup from assessment_details (same source as DamagedPartsModal)
                  const getPartIntensity = (part) => {
                    if (part.Intensity) return parseInt(part.Intensity);
                    if (part.intensity) return parseInt(part.intensity);
                    if (part.damage_intensity) return parseInt(part.damage_intensity);
                    if (part.severity) return parseInt(part.severity);
                    if (part.damage_severity) return parseInt(part.damage_severity);
                    for (const img of (part.damage_images || [])) {
                      const v = img.Intensity || img.intensity || img.damage_intensity || img.severity || img.damage_severity;
                      if (v) return parseInt(v);
                    }
                    return 0;
                  };
                  const severityMap = {};
                  (assessmentData.assessment_details || []).forEach((part) => {
                    if (!part.part_name) return;
                    const intensity = getPartIntensity(part);
                    const sev = intensity > 8 ? "severe" : intensity >= 1 ? "moderate" : "minor";
                    severityMap[part.part_name.toLowerCase()] = sev;
                  });

                  const SEVERITY_STYLES = {
                    severe:   { bg: "#fee2e2", border: "#ef4444" },
                    moderate: { bg: "#ffedd5", border: "#f97316" },
                    minor:    { bg: "#fef9c3", border: "#eab308" },
                  };

                  return (
                    <div className="bg-background-secondary rounded-[8px] px-[32px] py-[24px] w-full">
                      {/* Legend */}
                      <div className="flex items-center justify-between mb-4">
                        <h3
                          className="font-semibold text-text-primary text-lg tracking-[-0.18px]"
                          style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                        >
                          Damaged Parts List
                        </h3>
                        <div className="flex gap-5">
                          {[
                            { label: "Severe",   count: assessmentData.summary.severe_damage   || 0, sev: "severe" },
                            { label: "Moderate", count: assessmentData.summary.moderate_damage || 0, sev: "moderate" },
                            { label: "Minor",    count: assessmentData.summary.minor_damage    || 0, sev: "minor" },
                          ].map(({ label, count, sev }) => (
                            <div key={sev} className="flex items-center gap-1.5">
                              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: SEVERITY_STYLES[sev].border }} />
                              <span className="text-xs text-gray-600 font-medium">{label}: {count}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {assessmentData.summary.damaged_parts_list.map((part, index) => {
                          const sev = severityMap[part.toLowerCase()] || "minor";
                          const s = SEVERITY_STYLES[sev];
                          return (
                            <div
                              key={index}
                              className="rounded-[4px] px-3 py-2"
                              style={{ backgroundColor: s.bg, border: `1px solid ${s.border}` }}
                            >
                              <span
                                className="font-medium text-xs text-[#111827] tracking-[-0.12px]"
                                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                              >
                                {part}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
            </div>
          </div>
        )}

        {/* Images Tab Content */}
        {tab === "images" && pageConfig.tabs.images.enabled && (
          <div className="p-2 lg:p-4 pb-8 lg:pb-12">
            {/* Trinetra Images Header */}
            <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h2
                className="font-semibold text-text-primary text-lg lg:text-xl tracking-[-0.2px]"
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                {pageConfig.tabs.images.sections.inventoryOfDamages.title}
              </h2>
              <div className="bg-background-secondary rounded-[8px] px-3 py-2 shadow-sm flex items-center gap-2 w-fit">
                <span
                  className="font-medium text-text-secondary text-xs uppercase tracking-[-0.12px]"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  Uploaded Images
                </span>
                <span
                  className="font-semibold text-text-primary text-sm tracking-[-0.14px]"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  {getUploadedImageCount()}
                </span>
              </div>
            </div>

            {/* Image Cards Grid */}
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <img
                    src={loadingIcon}
                    alt="Loading"
                    className="w-8 h-8 animate-spin mx-auto mb-4"
                  />
                  <p className="text-gray-600">Loading damage inventory...</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {/* Exteriors Card */}
                {pageConfig.tabs.images.sections.categories.exteriors
                  .enabled && (
                    <div className="bg-background-secondary rounded-[8px] overflow-hidden shadow-sm">
                      <div
                        className={`relative ${categorizeUploads().exteriors.length > 0
                            ? "cursor-pointer"
                            : "cursor-default"
                          }`}
                        onClick={() => {
                          const categories = categorizeUploads();
                          if (categories.exteriors.length > 0) {
                            openLightbox(categories.exteriors, 0);
                          }
                        }}
                      >
                        {(() => {
                          const categories = categorizeUploads();
                          if (categories.exteriors.length > 0) {
                            return (
                              <img
                                src={categories.exteriors[0].imageUrl}
                                alt="Exterior damage view"
                                className="w-full h-[30vh] max-h-[280px] object-cover hover:opacity-90 transition-opacity"
                                onError={(e) => {
                                  e.target.src = exteriorDamage1;
                                }}
                              />
                            );
                          } else {
                            return (
                              <div className="w-full h-[30vh] max-h-[280px] bg-gray-100 flex flex-col items-center justify-center">
                                <svg
                                  className="w-16 h-16 text-gray-400 mb-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                                <p className="text-text-secondary text-sm font-medium">
                                  No images available
                                </p>
                                <p className="text-text-tertiary text-xs mt-1">
                                  User did not upload
                                </p>
                              </div>
                            );
                          }
                        })()}
                      </div>
                      <div className="p-4 lg:p-6">
                        <div className="mb-2">
                          <span
                            className="font-normal text-text-secondary text-xs tracking-[-0.12px] uppercase"
                            style={{
                              fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                            }}
                          >
                            USER CAPTURED
                          </span>
                        </div>
                        <h3
                          className="font-semibold text-text-primary text-base tracking-[-0.16px]"
                          style={{
                            fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                          }}
                        >
                          {
                            pageConfig.tabs.images.sections.categories.exteriors
                              .label
                          }{" "}
                          <span className="text-text-secondary">
                            • {categorizeUploads().exteriors.length} images
                          </span>
                        </h3>
                      </div>
                    </div>
                  )}

                {/* Close-ups Card */}
                {pageConfig.tabs.images.sections.categories.closeups
                  .enabled && (
                    <div className="bg-background-secondary rounded-[8px] overflow-hidden shadow-sm">
                      <div
                        className={`relative ${categorizeUploads().closeups.length > 0
                            ? "cursor-pointer"
                            : "cursor-default"
                          }`}
                        onClick={() => {
                          const categories = categorizeUploads();
                          if (categories.closeups.length > 0) {
                            openLightbox(categories.closeups, 0);
                          }
                        }}
                      >
                        {(() => {
                          const categories = categorizeUploads();
                          if (categories.closeups.length > 0) {
                            return (
                              <img
                                src={categories.closeups[0].imageUrl}
                                alt="Close-up damage view"
                                className="w-full h-[30vh] max-h-[280px] object-cover hover:opacity-90 transition-opacity"
                                onError={(e) => {
                                  e.target.src = closeupDamage1;
                                }}
                              />
                            );
                          } else {
                            return (
                              <div className="w-full h-[30vh] max-h-[280px] bg-gray-100 flex flex-col items-center justify-center">
                                <svg
                                  className="w-16 h-16 text-gray-400 mb-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                                <p className="text-text-secondary text-sm font-medium">
                                  No images available
                                </p>
                                <p className="text-text-tertiary text-xs mt-1">
                                  User did not upload
                                </p>
                              </div>
                            );
                          }
                        })()}
                      </div>
                      <div className="p-4 lg:p-6">
                        <div className="mb-2">
                          <span
                            className="font-normal text-text-secondary text-xs tracking-[-0.12px] uppercase"
                            style={{
                              fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                            }}
                          >
                            USER CAPTURED
                          </span>
                        </div>
                        <h3
                          className="font-semibold text-text-primary text-base tracking-[-0.16px]"
                          style={{
                            fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                          }}
                        >
                          {
                            pageConfig.tabs.images.sections.categories.closeups
                              .label
                          }{" "}
                          <span className="text-text-secondary">
                            • {categorizeUploads().closeups.length} images
                          </span>
                        </h3>
                      </div>
                    </div>
                  )}

                {/* Identifiers Card */}
                {pageConfig.tabs.images.sections.categories.identifiers
                  .enabled && (
                    <div className="bg-background-secondary rounded-[8px] overflow-hidden shadow-sm md:col-span-2 xl:col-span-1">
                      <div
                        className={`relative ${categorizeUploads().identifiers.length > 0
                            ? "cursor-pointer"
                            : "cursor-default"
                          }`}
                        onClick={() => {
                          const categories = categorizeUploads();
                          if (categories.identifiers.length > 0) {
                            openLightbox(categories.identifiers, 0);
                          }
                        }}
                      >
                        {(() => {
                          const categories = categorizeUploads();
                          if (categories.identifiers.length > 0) {
                            return (
                              <img
                                src={categories.identifiers[0].imageUrl}
                                alt="Identifier damage view"
                                className="w-full h-[30vh] max-h-[280px] object-cover hover:opacity-90 transition-opacity"
                                onError={(e) => {
                                  e.target.src = identifierDamage1;
                                }}
                              />
                            );
                          } else {
                            return (
                              <div className="w-full h-[30vh] max-h-[280px] bg-gray-100 flex flex-col items-center justify-center">
                                <svg
                                  className="w-16 h-16 text-gray-400 mb-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                                <p className="text-text-secondary text-sm font-medium">
                                  No images available
                                </p>
                                <p className="text-text-tertiary text-xs mt-1">
                                  User did not upload
                                </p>
                              </div>
                            );
                          }
                        })()}
                      </div>
                      <div className="p-4 lg:p-6">
                        <div className="mb-2">
                          <span
                            className="font-normal text-text-secondary text-xs tracking-[-0.12px] uppercase"
                            style={{
                              fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                            }}
                          >
                            USER CAPTURED
                          </span>
                        </div>
                        <h3
                          className="font-semibold text-text-primary text-base tracking-[-0.16px]"
                          style={{
                            fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                          }}
                        >
                          {
                            pageConfig.tabs.images.sections.categories.identifiers
                              .label
                          }{" "}
                          <span className="text-text-secondary">
                            • {categorizeUploads().identifiers.length} images
                          </span>
                        </h3>
                      </div>
                    </div>
                  )}
              </div>
            )}

            {/* Additional Image Grid for Full Gallery */}
            <div className="mt-8">
              <div className="mb-4 pb-2 border-b border-border-secondary">
                {/* 2-Tab Toggle Switch */}
                <div className="flex bg-gray-100 p-0.5 rounded-lg w-fit border border-gray-200">
                  <button
                    type="button"
                    className={`px-3 py-1.5 rounded-md text-sm font-semibold tracking-[-0.1px] transition-all flex items-center gap-1.5 ${damageTab === "inventory"
                        ? "bg-white text-text-primary shadow-sm"
                        : "text-text-secondary hover:text-text-primary"
                      }`}
                    onClick={() => setDamageTab("inventory")}
                  >
                    <span>Inventory of Damages</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${damageTab === "inventory"
                        ? "bg-gray-100 text-text-secondary"
                        : "bg-gray-200 bg-opacity-65 text-text-secondary"
                      }`}>
                      {getInventoryOfDamagesImages().length}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1.5 rounded-md text-sm font-semibold tracking-[-0.1px] transition-all flex items-center gap-1.5 ${damageTab === "part-wise"
                        ? "bg-white text-text-primary shadow-sm"
                        : "text-text-secondary hover:text-text-primary"
                      }`}
                    onClick={() => setDamageTab("part-wise")}
                  >
                    <span>Part-wise Damages</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${damageTab === "part-wise"
                        ? "bg-gray-100 text-text-secondary"
                        : "bg-gray-200 bg-opacity-65 text-text-secondary"
                      }`}>
                      {getPartWiseDamageImages().length}
                    </span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {/* Dynamic image grid based on API data */}
                {assessmentData ? (
                  (() => {
                    const processedImages = damageTab === "inventory"
                      ? getInventoryOfDamagesImages()
                      : getPartWiseDamageImages();

                    if (processedImages.length === 0) {
                      return (
                        <div className="col-span-full text-center py-8">
                          <p className="text-text-secondary text-sm">
                            {damageTab === "inventory"
                              ? "No inventory damage images available"
                              : "No part-wise damage images available"}
                          </p>
                        </div>
                      );
                    }

                    return processedImages.map((imageData, index) => (
                      <div
                        key={`damage-image-${index}-${getImageIdentity(imageData)}`}
                        className="bg-background-secondary rounded-[4px] overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                        title={`${imageData.title || imageData.side} (${imageData.source})`}
                        onClick={() => {
                          const allImages = processedImages.map((img) => ({
                            id: img.id,
                            imageUrl: img.imageUrl,
                            originalImageUrl: img.originalImageUrl,
                            title: img.title || img.side,
                            side: img.side,
                            source: img.source,
                          }));

                          openLightbox(allImages, index, true); // Mark as damage images
                        }}
                      >
                        <img
                          src={imageData.imageUrl}
                          alt={`${imageData.title || imageData.side} damage view`}
                          className="w-full h-[80px] lg:h-[100px] object-cover"
                          onError={(e) => {
                            console.error(
                              `Failed to load image: ${imageData.imageUrl}`,
                            );
                            e.target.style.display = "none";
                          }}
                        />
                        <div className="p-1 text-center">
                          <span className="text-text-secondary text-xs lg:text-xs uppercase font-medium">
                            {imageData.title || imageData.side || "Damage"}
                          </span>
                        </div>
                      </div>
                    ));
                  })()
                ) : isLoading || assessmentData === null ? (
                  <div className="col-span-full text-center py-8">
                    <img
                      src={loadingIcon}
                      alt="Loading"
                      className="w-8 h-8 animate-spin mx-auto mb-4"
                    />
                    <p className="text-gray-600">Loading damage images...</p>
                  </div>
                ) : (
                  <div className="col-span-full text-center py-8">
                    <p className="text-text-secondary text-sm">
                      No damage images available
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab Content */}
        {tab === "documents" &&
          pageConfig.tabs.documents.enabled &&
          isDocumentsTabEnabled && (
            <div className="p-2 lg:p-3 pb-8 lg:pb-12">
              <div className="bg-background-secondary rounded-[8px] shadow-sm">
                {/* Documents Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-2 lg:gap-3 mb-4">
                  {/* Registration Certificate */}
                  {pageConfig.tabs.documents.documentTypes.registrationCertificate
                    .enabled && (
                      <div className="flex flex-col items-center p-2 lg:p-3 bg-white rounded-[8px] border border-border-light hover:shadow-md transition-shadow cursor-pointer">
                        <div className="overflow-clip size-[32px] lg:size-[36px] mb-2">
                          <img
                            alt="Registration Certificate"
                            className="block max-w-none size-full"
                            src={documentIcon}
                          />
                        </div>
                        <div
                          className="font-medium text-text-primary text-xs lg:text-sm text-center leading-tight"
                          style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                        >
                          {
                            pageConfig.tabs.documents.documentTypes
                              .registrationCertificate.label
                          }
                        </div>
                      </div>
                    )}

                  {/* Insurance Proof */}
                  {pageConfig.tabs.documents.documentTypes.insuranceProof
                    .enabled && (
                      <div className="flex flex-col items-center p-2 lg:p-3 bg-white rounded-[8px] border border-border-light hover:shadow-md transition-shadow cursor-pointer">
                        <div className="overflow-clip size-[32px] lg:size-[36px] mb-2">
                          <img
                            alt="Insurance Proof"
                            className="block max-w-none size-full"
                            src={documentIcon}
                          />
                        </div>
                        <div
                          className="font-medium text-text-primary text-xs lg:text-sm text-center leading-tight"
                          style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                        >
                          {
                            pageConfig.tabs.documents.documentTypes.insuranceProof
                              .label
                          }
                        </div>
                      </div>
                    )}

                  {/* PUC Certificate */}
                  {pageConfig.tabs.documents.documentTypes.pucCertificate
                    .enabled && (
                      <div className="flex flex-col items-center p-2 lg:p-3 bg-white rounded-[8px] border border-border-light hover:shadow-md transition-shadow cursor-pointer">
                        <div className="overflow-clip size-[32px] lg:size-[36px] mb-2">
                          <img
                            alt="PUC Certificate"
                            className="block max-w-none size-full"
                            src={documentIcon}
                          />
                        </div>
                        <div
                          className="font-medium text-text-primary text-xs lg:text-sm text-center leading-tight"
                          style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                        >
                          {
                            pageConfig.tabs.documents.documentTypes.pucCertificate
                              .label
                          }
                        </div>
                      </div>
                    )}

                  {/* Road Tax Receipt */}
                  {pageConfig.tabs.documents.documentTypes.roadTaxReceipt
                    .enabled && (
                      <div className="flex flex-col items-center p-2 lg:p-3 bg-white rounded-[8px] border border-border-light hover:shadow-md transition-shadow cursor-pointer">
                        <div className="overflow-clip size-[32px] lg:size-[36px] mb-2">
                          <img
                            alt="Road Tax Receipt"
                            className="block max-w-none size-full"
                            src={documentIcon}
                          />
                        </div>
                        <div
                          className="font-medium text-text-primary text-xs lg:text-sm text-center leading-tight"
                          style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                        >
                          {
                            pageConfig.tabs.documents.documentTypes.roadTaxReceipt
                              .label
                          }
                        </div>
                      </div>
                    )}


                  {/* Fitness Certificate */}
                  {pageConfig.tabs.documents.documentTypes.fitnessCertificate
                    .enabled && (
                      <div className="flex flex-col items-center p-2 lg:p-3 bg-white rounded-[8px] border border-border-light hover:shadow-md transition-shadow cursor-pointer">
                        <div className="overflow-clip size-[32px] lg:size-[36px] mb-2">
                          <img
                            alt="Fitness Certificate"
                            className="block max-w-none size-full"
                            src={documentIcon}
                          />
                        </div>
                        <div
                          className="font-medium text-text-primary text-xs lg:text-sm text-center leading-tight"
                          style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                        >
                          {
                            pageConfig.tabs.documents.documentTypes
                              .fitnessCertificate.label
                          }
                        </div>
                      </div>
                    )}
                </div>

                {/* Download All Button */}
                {pageConfig.tabs.documents.showDownloadAll && (
                  <div className="flex justify-center">
                    <button
                      onClick={downloadPDFReport}
                      disabled={isReportDownloading}
                      className={`bg-background-primary flex gap-[6px] items-center justify-center px-4 py-2 rounded-[8px] border border-border-secondary transition-colors ${isReportDownloading
                          ? "opacity-60 cursor-not-allowed"
                          : "hover:opacity-90"
                        }`}
                    >
                      <div className="overflow-clip size-[20px]">
                        <img
                          alt={
                            isReportDownloading ? "Downloading" : "Download All"
                          }
                          className={`block max-w-none size-full ${isReportDownloading ? "animate-spin" : ""
                            }`}
                          src={isReportDownloading ? loadingIcon : downloadIcon}
                        />
                      </div>
                      <span
                        className="font-medium text-text-primary text-sm lg:text-base"
                        style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                      >
                        {isReportDownloading
                          ? "Downloading"
                          : pageConfig.tabs.documents.downloadAllLabel}
                      </span>
                    </button>
                  </div>
                )}

                {pageConfig.tabs.documents.documentTypes.pdfReport.enabled && (
                  <div className="mt-4 bg-white rounded-[8px] border border-border-light overflow-hidden">
                    <div className="px-4 py-3 border-b border-border-light">
                      <p
                        className="font-medium text-text-primary text-sm lg:text-base"
                        style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                      >
                        PDF Preview
                      </p>
                    </div>

                    {isDocumentsLoading ? (
                      <div className="h-[420px] lg:h-[560px] flex items-center justify-center bg-background-primary">
                        <div className="text-center">
                          <img
                            src={loadingIcon}
                            alt="Loading preview"
                            className="w-8 h-8 animate-spin mx-auto mb-4"
                          />
                          <p className="text-gray-600">Loading PDF preview...</p>
                        </div>
                      </div>
                    ) : documentsPreviewUrl ? (
                      <iframe
                        title="PDF preview"
                        src={documentsPreviewUrl}
                        className="w-full h-[420px] lg:h-[560px] bg-white"
                      />
                    ) : (
                      <div className="h-[420px] lg:h-[560px] flex items-center justify-center bg-background-primary px-6 text-center">
                        <p className="text-sm text-text-secondary">
                          {documentsPreviewError}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Point of Impact */}
        {tab === "poi" && pageConfig.tabs.poi?.enabled && !caseFailed && (
          <div className="p-2 lg:p-3 pb-8 lg:pb-12">
            {isPoiLoading ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <img
                    src={loadingIcon}
                    alt="Loading"
                    className="w-8 h-8 animate-spin mx-auto mb-4"
                  />
                  <p className="text-gray-600">Loading point of impact...</p>
                </div>
              </div>
            ) : poiData ? (
              <div className="bg-background-secondary rounded-[8px] shadow-sm p-3 lg:p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <div className="bg-white rounded-[8px] border border-border-light p-3 lg:p-4">
                    <div
                      className="text-[9.5px] font-semibold uppercase tracking-[0.06em] text-text-secondary mb-1"
                      style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                    >
                      Point of Impact
                    </div>
                    <div className="font-medium text-text-primary text-sm lg:text-base">
                      {titleCase(poiData.poi) ||
                        pageConfig.general.noDataMessage}
                    </div>
                  </div>
                  <div className="bg-white rounded-[8px] border border-border-light p-3 lg:p-4">
                    <div
                      className="text-[9.5px] font-semibold uppercase tracking-[0.06em] text-text-secondary mb-1"
                      style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                    >
                      Parts Affected
                    </div>
                    <div className="font-medium text-text-primary text-sm lg:text-base">
                      {titleCase(poiData.parts_affected) ||
                        pageConfig.general.noDataMessage}
                    </div>
                  </div>
                </div>

                {poiData.image && (
                  <div
                    className="bg-white rounded-[8px] border border-border-light overflow-hidden cursor-pointer"
                    onClick={() =>
                      openLightbox(
                        [
                          {
                            imageUrl: getMediaUrl(poiData.image),
                            name: "Point of Impact",
                          },
                        ],
                        0,
                      )
                    }
                  >
                    <img
                      src={getMediaUrl(poiData.image)}
                      alt="Point of impact"
                      className="w-full max-h-[60vh] object-contain bg-background-primary hover:opacity-90 transition-opacity"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center min-h-[400px]">
                <p className="text-sm text-text-secondary">
                  {pageConfig.general.noDataMessage}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Digital Passport */}
        {tab === "passport" && pageConfig.tabs.digitalPassport?.enabled && !caseFailed && (
          <div className="p-2 lg:p-3 pb-8 lg:pb-12">
            {isPassportLoading ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <img
                    src={loadingIcon}
                    alt="Loading"
                    className="w-8 h-8 animate-spin mx-auto mb-4"
                  />
                  <p className="text-gray-600">Loading digital passport...</p>
                </div>
              </div>
            ) : passportData ? (
              <div className="bg-background-secondary rounded-[8px] shadow-sm p-3 lg:p-4">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                  {[
                    {
                      label: "Reference Number",
                      value: passportData.ref_num || caseId,
                    },
                    {
                      label: "Source Name",
                      value: passportData.source_name || apiSourceName,
                    },
                    {
                      label: "Number Plate",
                      value: passportData.number_plate,
                    },
                    {
                      label: "Assessment Date",
                      value: passportData.inspection_date
                        ? new Date(
                          passportData.inspection_date,
                        ).toLocaleDateString(pageConfig.general.dateFormat)
                        : "",
                    },
                    {
                      label: "Damaged Parts",
                      value: passportDamages.length
                        ? titleCase(
                          passportDamages.map((d) => d.part_name).join(", "),
                        )
                        : "",
                    },
                    {
                      label: "Damage Types",
                      value: passportDamages.length
                        ? titleCase(
                          [
                            ...new Set(
                              passportDamages
                                .map((d) => d.damage_type)
                                .filter(Boolean),
                            ),
                          ].join(", "),
                        )
                        : "",
                    },
                  ].map((field) => (
                    <div
                      key={field.label}
                      className="bg-white rounded-[8px] border border-border-light p-3"
                    >
                      <div
                        className="text-[9.5px] font-semibold uppercase tracking-[0.06em] text-text-secondary mb-1"
                        style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                      >
                        {field.label}
                      </div>
                      <div className="font-medium text-text-primary text-xs lg:text-sm break-words">
                        {field.value || pageConfig.general.noDataMessage}
                      </div>
                    </div>
                  ))}
                </div>

                {passportDamages.length > 0 && (
                  <div className="bg-white rounded-[8px] border border-border-light overflow-hidden">
                    <div className="px-4 py-3 border-b border-border-light">
                      <p
                        className="font-medium text-text-primary text-sm lg:text-base"
                        style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                      >
                        Damaged Parts
                      </p>
                    </div>
                    <div className="divide-y divide-border-light">
                      {passportDamages.map((damage, damageIndex) => {
                        const images = Array.isArray(damage.images)
                          ? damage.images
                          : damage.images
                            ? [damage.images]
                            : [];
                        const lightboxItems = images.map((img) => ({
                          imageUrl: getMediaUrl(img),
                          name: titleCase(damage.part_name),
                        }));

                        return (
                          <div
                            key={`${damage.part_name}-${damageIndex}`}
                            className="px-4 py-3"
                          >
                            <div className="flex items-baseline justify-between gap-3 mb-2">
                              <span className="font-medium text-text-primary text-sm">
                                {titleCase(damage.part_name)}
                              </span>
                              {damage.damage_type && (
                                <span className="text-xs text-text-secondary">
                                  {titleCase(damage.damage_type)}
                                </span>
                              )}
                            </div>
                            {images.length > 0 && (
                              <div className="flex gap-2 flex-wrap">
                                {images.map((img, imgIndex) => (
                                  <img
                                    key={`${img}-${imgIndex}`}
                                    src={getMediaUrl(img)}
                                    alt={`${damage.part_name} ${imgIndex + 1}`}
                                    className="size-16 lg:size-20 object-cover rounded-[6px] border border-border-light cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() =>
                                      openLightbox(lightboxItems, imgIndex)
                                    }
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center min-h-[400px]">
                <p className="text-sm text-text-secondary">
                  {pageConfig.general.noDataMessage}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <LocationCheckModal
        assessmentData={assessmentData}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />

      <OverviewIdentifiedModal
        isOpen={overviewModalType === "parts"}
        onClose={closeOverviewModal}
        title="Parts Identified"
        emptyMessage="No parts data available."
        sections={partsModalSections}
      />

      <OverviewIdentifiedModal
        isOpen={overviewModalType === "sides"}
        onClose={closeOverviewModal}
        title="Sides Identified"
        emptyMessage="No sides data available."
        sections={sidesModalSections}
      />

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        images={lightboxImages}
        currentIndex={lightboxCurrentIndex}
        onClose={closeLightbox}
        assessmentData={assessmentData}
        isDamageImages={lightboxIsDamageImages}
      />

      {/* Damaged Parts Modal */}
      <DamagedPartsModal
        isOpen={damagedPartsModalState.isOpen}
        onClose={closeDamagedPartsModal}
        assessmentData={assessmentData}
        initialFilter={damagedPartsModalState.initialFilter}
        initialSeverityFilter={damagedPartsModalState.initialSeverityFilter}
        onRefreshAssessment={refreshAssessmentData}
        setIsSearchActive={setIsSearchActive}
        isSearchActive={isSearchActive}
        isApproved={isApproved}
      />

      <ApproveCommentModal
        isOpen={approveCommentOpen}
        onClose={() => setApproveCommentOpen(false)}
        onSubmit={handleApproveSubmit}
        userId={getUserProfile()?.email || clientConfig.fallbackEmail}
        numPlate={
          assessmentData?.info?.num_plate?.trim()
            ? assessmentData.info.num_plate.trim()
            : null
        }
        title="Add a note to complete the process"
        submitLabel="Submit"
      />
    </div>
  );
}
